import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Alert,
  FormControlLabel,
  Switch,
  Stack,
  TableHead,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  PlayArrow as PlayArrowIcon,
  Stop as StopIcon,
  Settings as SettingsIcon,
  AutoFixHigh as AutoFixHighIcon,
  GraphicEq as GraphicEqIcon,
  CloudUpload as CloudUploadIcon,
  Upload as UploadIcon,
  Clear as ClearIcon,
  VolumeUp as VolumeUpIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useThemeColors } from '../theme/useThemeColors';
import { SettingsDialog } from './SettingsDialog';
import { ScriptManager } from './ScriptManager';
import { LimiterSettingsDialog } from './LimiterSettingsDialog';
import { styled } from '@mui/material/styles';
import { WaveformEditorDialog } from './WaveformEditorDialog';
import VUMeter from './VUMeter';
import { useSnackbar } from 'notistack';
import { SERVER_BASE_URL, getDefaultOutroDuration, getDefaultOutroAudioUrl, getVoLanguage, setVoLanguage, VoLanguage } from '../config';
import { chartDataService } from '../services/chartDataService';
import { useGoogleSheets } from '../context/GoogleSheetsContext';

// Hidden input for file upload
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});

// Define TranscriptionResult interface
interface TranscriptionResult {
  text: string;
  segments: Array<{
    start: number;
    end: number;
    text: string;
  }>;
  timestamps: {
    [key: string]: {
      inPoint: number;
      outPoint: number;
    }
  };
  // Script lines the alignment couldn't find in the audio (no in/out returned).
  missingLines?: string[];
  fileUrl?: string;
  fileId?: string;
  words?: Array<{
    word: string;
    normalizedWord: string;
    startTime: number;
    endTime: number;
  }>;
  peaks?: number[];
  teaseDetection?: {
    hasTease: boolean;
    teaseContent: string;
    introStartWord: string;
    confidence: string;
  };
}


// Cell mapping assumes VO data is in column L of the target sheet
const CELL_MAP: { [key: string]: string } = {
  '10': 'L2',
  '9': 'L3',
  '8': 'L4',
  '7': 'L5',
  '6': 'L6',
  '5': 'L7',
  '4': 'L8',
  '3': 'L9',
  '2': 'L10',
  '1': 'L11',
  'Intro': 'L12',
  'Outro': 'L13',
  'Tease': 'L15'
};

// NOTE: VO In/Out and gain are NOT addressed by these per-key letter maps. VO
// In/Out map to the inPoint/outPoint fields (N/O); line in/out to lineInPoint/
// lineOutPoint (T/U); line gain to lineGain (P, via LINE_GAIN_MAP). See COL_TO_FIELD.

// Add mapping for Line VO URLs (Column S)
const LINE_VO_MAP: { [key: string]: string } = {
  'Intro': 'S12',
  '10': 'S2',
  '9': 'S3',
  '8': 'S4',
  '7': 'S5',
  '6': 'S6',
  '5': 'S7',
  '4': 'S8',
  '3': 'S9',
  '2': 'S10',
  '1': 'S11',
  'Outro': 'S13',
  'Tease': 'S15'
};

// Add mapping for Uploaded VO URLs (Column V) - preserves uploaded URLs across mode switches
const UPLOADED_VO_MAP: { [key: string]: string } = {
  'Intro': 'V12',
  '10': 'V2',
  '9': 'V3',
  '8': 'V4',
  '7': 'V5',
  '6': 'V6',
  '5': 'V7',
  '4': 'V8',
  '3': 'V9',
  '2': 'V10',
  '1': 'V11',
  'Outro': 'V13',
  'Tease': 'V15'
};

// Add mapping for Column P (Line Gain settings)
const LINE_GAIN_MAP: { [key: string]: string } = {
  'Intro': 'P12',
  '10': 'P2',
  '9': 'P3',
  '8': 'P4',
  '7': 'P5',
  '6': 'P6',
  '5': 'P7',
  '4': 'P8',
  '3': 'P9',
  '2': 'P10',
  '1': 'P11',
  'Outro': 'P13',
  'Tease': 'P15'
};

// Global VO gain is a week-level setting ('globalGain'); monolithic processed-audio
// URL is the week-level 'processedVoUrl'; the week's human date is 'dateString'.

// Map an editor key ('10'..'1' | 'Intro' | 'Outro' | 'Tease') to a chart-data VO
// section ('position_10'..'position_1' | 'intro' | 'outro' | 'tease'). Positions
// 10..1 become position_10..position_1; the named keys lowercase to their section.
const keyToSection = (key: string): string => {
  if (key === 'Intro') return 'intro';
  if (key === 'Outro') return 'outro';
  if (key === 'Tease') return 'tease';
  // numeric position key ('10'..'1')
  return `position_${key}`;
};

// VO column letters → chart-data VO field names. L=scriptText, M=audioUrl,
// N=inPoint, O=outPoint, P=lineGain, S=lineVoUrl, T=lineInPoint, U=lineOutPoint,
// V=uploadedVoUrl. Used by the write paths in place of the old A1 cell notation.
const COL_TO_FIELD: { [col: string]: string } = {
  L: 'scriptText',
  M: 'audioUrl',
  N: 'inPoint',
  O: 'outPoint',
  P: 'lineGain',
  S: 'lineVoUrl',
  T: 'lineInPoint',
  U: 'lineOutPoint',
  V: 'uploadedVoUrl',
};

// Define the desired display order
const DISPLAY_ORDER = [
  'Tease',
  'Intro',
  '10', '9', '8', '7', '6', '5', '4', '3', '2', '1',
  'Outro'
];

interface VOManagerProps {
  spreadsheetId?: string;
}

// getDefaultOutroAudioUrl now lives in ../config (shared with OutputManager).
const getDefaultOutroText = (outroName: string) =>
  `IF YOU WANT MORE ${(outroName || 'BILLBOARD').toUpperCase()}, MAKE SURE YOU HIT THE SUBSCRIBE BUTTON AND RING THE BELL TO BE NOTIFIED ON ALL OUR LATEST VIDEOS`;

// Add this constant near the top where other constants are defined
const API_BASE_URL = SERVER_BASE_URL;

// We no longer need to track or write to Voice Over URL cell

export const VOManager: React.FC<VOManagerProps> = ({ spreadsheetId: propSpreadsheetId }) => {
  const navigate = useNavigate();
  const { spreadsheetId: routeSpreadsheetId } = useParams();
  const { currentChartConfig } = useGoogleSheets();
  const { themeColor, themeHoverColor } = useThemeColors();
  const [activeSpreadsheetId, setActiveSpreadsheetId] = useState<string | null>(routeSpreadsheetId || propSpreadsheetId || null);
  // Bilingual: which VO language the editor is operating on (per-sheet, default 'en').
  // Only surfaced when the chart is bilingual; shared data is identical in any language.
  const isBilingual = !!currentChartConfig?.bilingual;
  const [activeLanguage, setActiveLanguageState] = useState<VoLanguage>(() => getVoLanguage(routeSpreadsheetId || propSpreadsheetId || null));
  const [voData, setVoData] = useState<{ [key: string]: string }>({});
  const [generatedAudio, setGeneratedAudio] = useState<{ [key: string]: string | null }>({});
  const [generatedInPoints, setGeneratedInPoints] = useState<{ [key: string]: string }>({});
  const [generatedOutPoints, setGeneratedOutPoints] = useState<{ [key: string]: string }>({});
  const [editingCell, setEditingCell] = useState<{ key: string; value: string } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [isGeneratingAll, setIsGeneratingAll] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [playingKey, setPlayingKey] = useState<string | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const compressorNodeRef = useRef<DynamicsCompressorNode | null>(null);
  const gainNodeRef = useRef<GainNode | null>(null);
  const sourceNodeRef = useRef<MediaElementAudioSourceNode | null>(null);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [scriptDialogOpen, setScriptDialogOpen] = useState(false);
  const [voiceSettings, setVoiceSettings] = useState({
    voiceId: localStorage.getItem('voiceSettings_voiceId') || 'exkLoDgg9PFCos2rzsiW',
    stability: parseFloat(localStorage.getItem('voiceSettings_stability') || '0.5'),
    similarityBoost: parseFloat(localStorage.getItem('voiceSettings_similarityBoost') || '0.75'),
    style: parseFloat(localStorage.getItem('voiceSettings_style') || '0.6'),
    speed: parseFloat(localStorage.getItem('voiceSettings_speed') || '1.1'),
  });
  const [isGenerateMode, setIsGenerateMode] = useState<boolean>(() => {
    const savedMode = localStorage.getItem('voManagerMode');
    return savedMode ? savedMode === 'generate' : false; // Default to Upload VO mode
  });
  const [voInPoints, setVoInPoints] = useState<{ [key: string]: string }>({});
  const [voOutPoints, setVoOutPoints] = useState<{ [key: string]: string }>({});
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [limiterSettings, setLimiterSettings] = useState({
    gain: parseFloat(localStorage.getItem('limiterSettings_gain') || '0'), // This is the main makeup gain
  });
  const [showLimiterDialog, setShowLimiterDialog] = useState(false);
  const [processedAudioUrl, setProcessedAudioUrl] = useState<string | null>(null);
  const audioElementRef = useRef<HTMLAudioElement | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [isUploadSuccess, setIsUploadSuccess] = useState<boolean>(false);
  const [transcriptionResult, setTranscriptionResult] = useState<TranscriptionResult | null>(null);
  // Re-add peaks state
  const [processedAudioPeaks, setProcessedAudioPeaks] = useState<number[] | null>(null);

  // Add state to manage opening the editor after data is set
  const [pendingEditorKey, setPendingEditorKey] = useState<string | null>(null);

  // Add new state for the waveform editor
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  
  // Add sheetDate state for S3 folder structure
  const [sheetDate, setSheetDate] = useState<string | null>(null);
  // Define state for waveform editor
  interface EditorData {
    lineKey: string;
    initialInPoint: number;
    initialOutPoint: number;
    initialGain?: number; // Add initialGain to the interface
    audioUrl: string | null;
    songTitle?: string;
    songArtist?: string;
  }
  const [editorData, setEditorData] = useState<EditorData | null>(null);

  // Add state for song titles and artists
  const [songTitles, setSongTitles] = useState<{ [key: string]: string }>({});
  const [songArtists, setSongArtists] = useState<{ [key: string]: string }>({});

  // Add new ref for the analyser node
  // const analyserNodeRef = useRef<AnalyserNode | null>(null); // Replaced by state
  const [activeAnalyser, setActiveAnalyser] = useState<AnalyserNode | null>(null);

  // Add new state for individual line audio files
  const [lineAudioFiles, setLineAudioFiles] = useState<{ [key: string]: string }>({});
  const [uploadedAudioFiles, setUploadedAudioFiles] = useState<{ [key: string]: string }>({});
  const [isUploadingLine, setIsUploadingLine] = useState<string | null>(null);
  const [isGeneratingLine, setIsGeneratingLine] = useState<string | null>(null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  // Add state for per-line gain settings
  const [lineGainSettings, setLineGainSettings] = useState<{ [key: string]: number }>({});

  // ATSC normalization function
  const processAudioWithATSCNormalization = async (file: File): Promise<Blob> => {
    // Create audio context if not exists
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }

    const audioContext = audioContextRef.current;

    // Read file as array buffer
    const arrayBuffer = await file.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // TEMPORARILY DISABLED: All volume adjustments (gain, limiter, compressor)
    // Just convert to WAV without any processing
    console.log(`[Audio Processing] Converting to WAV without volume adjustments - Input RMS analysis disabled`);

    /* COMMENTED OUT: ATSC Normalization processing
    // Create offline context for processing
    const offlineContext = new OfflineAudioContext({
      numberOfChannels: audioBuffer.numberOfChannels,
      length: audioBuffer.length,
      sampleRate: audioBuffer.sampleRate
    });

    // Create source
    const source = offlineContext.createBufferSource();
    source.buffer = audioBuffer;

    // Step 1: Analyze RMS level to estimate loudness
    const channelData = audioBuffer.getChannelData(0);
    let sumSquares = 0;
    for (let i = 0; i < channelData.length; i++) {
      sumSquares += channelData[i] * channelData[i];
    }
    const rms = Math.sqrt(sumSquares / channelData.length);
    const rmsDb = 20 * Math.log10(rms);

    // Step 2: Calculate gain to bring to approximately -24 LUFS
    // Rough conversion: -24 LUFS ≈ -20dB RMS for speech content
    const targetRmsDb = -20;
    const gainAdjustmentDb = targetRmsDb - rmsDb;
    const gainAdjustment = Math.pow(10, gainAdjustmentDb / 20);

    console.log(`[ATSC Normalization] Input RMS: ${rmsDb.toFixed(2)}dB, Target: ${targetRmsDb}dB, Gain: ${gainAdjustmentDb.toFixed(2)}dB, Final: -6dB`);

    // Step 3: Apply gain adjustment
    const gainNode = offlineContext.createGain();
    gainNode.gain.value = gainAdjustment; // Apply calculated gain

    // Step 4: Create limiter to prevent clipping (safety net)
    const limiter = offlineContext.createDynamicsCompressor();
    limiter.threshold.setValueAtTime(-0.5, offlineContext.currentTime); // Catch peaks near 0dBFS
    limiter.knee.setValueAtTime(0, offlineContext.currentTime); // Hard knee for limiting
    limiter.ratio.setValueAtTime(20, offlineContext.currentTime); // 20:1 ratio (brick wall)
    limiter.attack.setValueAtTime(0.001, offlineContext.currentTime); // 1ms attack (very fast)
    limiter.release.setValueAtTime(0.1, offlineContext.currentTime); // 100ms release

    // Step 5: Create compressor for ATSC A/85 compliance
    const compressor = offlineContext.createDynamicsCompressor();
    compressor.threshold.setValueAtTime(-12, offlineContext.currentTime); // -12dB threshold
    compressor.knee.setValueAtTime(30, offlineContext.currentTime); // Soft knee
    compressor.ratio.setValueAtTime(12, offlineContext.currentTime); // 12:1 ratio
    compressor.attack.setValueAtTime(0.003, offlineContext.currentTime); // 3ms attack
    compressor.release.setValueAtTime(0.25, offlineContext.currentTime); // 250ms release

    // Step 6: Add -6dB reduction for headroom
    const headroomGain = offlineContext.createGain();
    headroomGain.gain.value = Math.pow(10, -6 / 20); // -6dB reduction

    // Connect nodes: source -> gain -> limiter -> compressor -> headroom reduction -> destination
    source.connect(gainNode);
    gainNode.connect(limiter);
    limiter.connect(compressor);
    compressor.connect(headroomGain);
    headroomGain.connect(offlineContext.destination);

    // Start rendering
    source.start(0);
    const processedBuffer = await offlineContext.startRendering();

    // Step 5: Analyze the processed output
    const processedChannelData = processedBuffer.getChannelData(0);

    // Find peak level
    let peak = 0;
    for (let i = 0; i < processedChannelData.length; i++) {
      peak = Math.max(peak, Math.abs(processedChannelData[i]));
    }
    const peakDb = 20 * Math.log10(peak);

    // Calculate output RMS
    let outputSumSquares = 0;
    for (let i = 0; i < processedChannelData.length; i++) {
      outputSumSquares += processedChannelData[i] * processedChannelData[i];
    }
    const outputRms = Math.sqrt(outputSumSquares / processedChannelData.length);
    const outputRmsDb = 20 * Math.log10(outputRms);

    console.log(`[ATSC Analysis] Output Peak: ${peakDb.toFixed(2)}dB, Output RMS: ${outputRmsDb.toFixed(2)}dB`);
    console.log(`[ATSC Analysis] Expected: Peak ≤ -5dB, RMS ≈ -20dB`);

    // Convert processed buffer to WAV blob
    return audioBufferToWav(processedBuffer);
    */

    // Convert original audio buffer to WAV blob without any processing
    return audioBufferToWav(audioBuffer);
  };

  // Convert audio buffer to WAV blob
  const audioBufferToWav = (buffer: AudioBuffer): Blob => {
    const numberOfChannels = buffer.numberOfChannels;
    const length = buffer.length;
    const sampleRate = buffer.sampleRate;
    const arrayBuffer = new ArrayBuffer(44 + length * numberOfChannels * 2);
    const view = new DataView(arrayBuffer);

    const writeString = (offset: number, string: string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };

    // WAV header
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + length * numberOfChannels * 2, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numberOfChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * numberOfChannels * 2, true);
    view.setUint16(32, numberOfChannels * 2, true);
    view.setUint16(34, 16, true);
    writeString(36, 'data');
    view.setUint32(40, length * numberOfChannels * 2, true);

    // Convert audio data
    let offset = 44;
    for (let i = 0; i < length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
        view.setInt16(offset, sample < 0 ? sample * 0x8000 : sample * 0x7FFF, true);
        offset += 2;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };

  // Effect to SAVE lineAudioFiles to localStorage when they change
  useEffect(() => {
    if (activeSpreadsheetId && Object.keys(lineAudioFiles).length > 0) {
      try {
        localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, JSON.stringify(lineAudioFiles));
        console.log(`[VOManager] Saved lineAudioFiles to localStorage for ${activeSpreadsheetId}:`, lineAudioFiles);
      } catch (error) {
          console.error(`[VOManager] Error saving lineAudioFiles to localStorage for ${activeSpreadsheetId}:`, error);
          enqueueSnackbar('Error saving line audio data locally. Storage might be full.', { variant: 'warning' });
      }
    } else if (activeSpreadsheetId && Object.keys(lineAudioFiles).length === 0) {
      // If lineAudioFiles becomes empty for an active spreadsheet, clear it from localStorage too
      // This prevents loading stale empty objects if all lines are cleared.
      const wasPreviouslySet = localStorage.getItem(`lineAudioFiles_${activeSpreadsheetId}`);
      if (wasPreviouslySet) { // Only remove if it was actually there
        localStorage.removeItem(`lineAudioFiles_${activeSpreadsheetId}`);
        console.log(`[VOManager] Cleared lineAudioFiles from localStorage for ${activeSpreadsheetId} as it became empty.`);
      }
    }
  }, [lineAudioFiles, activeSpreadsheetId, enqueueSnackbar]);

  // Add effect to save voice settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('voiceSettings_voiceId', voiceSettings.voiceId);
    localStorage.setItem('voiceSettings_stability', voiceSettings.stability.toString());
    localStorage.setItem('voiceSettings_similarityBoost', voiceSettings.similarityBoost.toString());
  }, [voiceSettings]);

  useEffect(() => {
    if (activeSpreadsheetId) {
      console.log(`[VOManager useEffect] Running cleanup effect for activeSpreadsheetId: ${activeSpreadsheetId}, limiterSettings.gain: ${limiterSettings.gain}`);
      const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
      const existingSettingsString = localStorage.getItem(storageKey);
      console.log(`[VOManager useEffect] Existing localStorage value:`, existingSettingsString);
      let gainToSave = String(limiterSettings.gain); // Use current global gain instead of defaulting to '0'
      console.log(`[VOManager useEffect] Initial gainToSave (from limiterSettings.gain): ${gainToSave}`);

      let existingSettings: any = {};
      if (existingSettingsString) {
        try {
          existingSettings = JSON.parse(existingSettingsString);
          console.log(`[VOManager useEffect] Parsed existing settings:`, existingSettings);
          if (existingSettings.makeupGain !== undefined) {
            gainToSave = String(existingSettings.makeupGain);
            console.log(`[VOManager useEffect] Using existing makeupGain: ${gainToSave}`);
          } else if (existingSettings.gain !== undefined) {
            gainToSave = String(existingSettings.gain);
            console.log(`[VOManager useEffect] Using existing gain: ${gainToSave}`);
          }
        } catch (error) {
          console.error(`[VOManager] Error parsing existing audioExportSettings for ${activeSpreadsheetId}:`, error);
        }
      } else {
        console.log(`[VOManager useEffect] No existing settings found, using limiterSettings.gain: ${gainToSave}`);
      }

      // Preserve existing line-specific gains while updating global gain
      const newSettings = {
        ...existingSettings, // Preserve lineGain_* entries
        makeupGain: gainToSave
      };
      localStorage.setItem(storageKey, JSON.stringify(newSettings));
      console.log(`[VOManager useEffect] Final settings saved to localStorage:`, newSettings);
    }
  }, [activeSpreadsheetId, limiterSettings.gain]);

  useEffect(() => {
    localStorage.setItem('voManagerMode', isGenerateMode ? 'generate' : 'upload');
  }, [isGenerateMode]);

  // Handle mode toggle: swap Column S URLs between generated and uploaded sets
  const handleModeToggle = async (checked: boolean) => {
    setIsGenerateMode(checked);

    if (!activeSpreadsheetId) return;

    // Collect one setVoField call per (section, field) change; awaited together below.
    const writes: Promise<void>[] = [];
    const sheetId = activeSpreadsheetId;
    const setField = (key: string, col: string, value: string) =>
      writes.push(chartDataService.setVoField(sheetId, keyToSection(key), COL_TO_FIELD[col], value, activeLanguage));

    if (checked) {
      // Switching TO generate mode: write generated URLs to lineVoUrl (S)
      const newLineAudioFiles: { [key: string]: string } = {};
      for (const key of Object.keys(generatedAudio)) {
        const url = generatedAudio[key];
        if (url) {
          // Strip cache-busting param for storage
          const cleanUrl = url.split('?')[0];
          newLineAudioFiles[key] = cleanUrl;
          if (LINE_VO_MAP[key]) setField(key, 'S', cleanUrl);
        }
      }
      // Clear lineVoUrl for keys that were in the old set but aren't in the new set
      for (const key of Object.keys(lineAudioFiles)) {
        if (!newLineAudioFiles[key] && LINE_VO_MAP[key]) {
          setField(key, 'S', '');
        }
      }
      // Restore generated in/out points to line in/out (T/U)
      const rowMap: { [key: string]: number } = {
        'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
        '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
      };
      for (const key of Object.keys(generatedInPoints)) {
        const row = rowMap[key];
        if (row && generatedInPoints[key] && generatedOutPoints[key]) {
          setField(key, 'T', generatedInPoints[key]);
          setField(key, 'U', generatedOutPoints[key]);
        }
      }
      // Also restore in/out points in state
      setVoInPoints(prev => ({ ...prev, ...generatedInPoints }));
      setVoOutPoints(prev => ({ ...prev, ...generatedOutPoints }));

      setLineAudioFiles(newLineAudioFiles);
      localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, JSON.stringify(newLineAudioFiles));
      console.log('[VOManager] Mode → Generate: swapped lineVoUrl to generated URLs, restored T/U in/out points');
    } else {
      // Switching TO upload mode: write uploaded URLs to lineVoUrl (S)
      const newLineAudioFiles: { [key: string]: string } = {};
      for (const key of Object.keys(uploadedAudioFiles)) {
        const url = uploadedAudioFiles[key];
        if (url) {
          newLineAudioFiles[key] = url;
          if (LINE_VO_MAP[key]) setField(key, 'S', url);
        }
      }
      // Clear lineVoUrl for keys that were in the old set but aren't in the new set
      for (const key of Object.keys(lineAudioFiles)) {
        if (!newLineAudioFiles[key] && LINE_VO_MAP[key]) {
          setField(key, 'S', '');
        }
      }

      // Clear line in/out (T/U) so the main in/out (N/O) timestamps are used for the
      // monolithic file. Generate mode writes per-clip in/out points to T/U which are
      // wrong for the full file.
      const rowMap: { [key: string]: number } = {
        'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
        '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
      };
      for (const key of Object.keys(rowMap)) {
        // Only clear T/U for lines that DON'T have a line-specific uploaded file
        if (!newLineAudioFiles[key]) {
          setField(key, 'T', '');
          setField(key, 'U', '');
        }
      }

      setLineAudioFiles(newLineAudioFiles);
      localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, JSON.stringify(newLineAudioFiles));
      console.log('[VOManager] Mode → Upload: swapped lineVoUrl to uploaded URLs, cleared T/U for non-override lines');
    }

    // Persist all VO field changes (lineVoUrl + T/U) to the DB.
    if (writes.length > 0) {
      try {
        await Promise.all(writes);
        console.log(`[VOManager] Mode toggle: updated ${writes.length} VO fields`);
      } catch (err) {
        console.error('[VOManager] Failed to update VO data on mode toggle:', err);
      }
    }

    // Reload VO data so in/out points come from the correct columns
    // (N/O for upload mode after T/U was cleared, T/U for generate mode)
    if (!checked) {
      await fetchVOData();
      console.log('[VOManager] Mode → Upload: reloaded VO data to restore N/O timestamps');
    }
  };

  useEffect(() => {
    if (propSpreadsheetId) {
      console.log('VOManager: Using spreadsheetId from props:', propSpreadsheetId);
      setActiveSpreadsheetId(propSpreadsheetId);
      localStorage.setItem('currentSpreadsheetId', propSpreadsheetId);
    } else {
      const storedId = localStorage.getItem('currentSpreadsheetId');
      if (storedId) {
        console.log('VOManager: Found spreadsheetId in localStorage:', storedId);
        setActiveSpreadsheetId(storedId);
      } else {
        console.warn('VOManager: No spreadsheetId found in localStorage. Select a date on the Charts page.');
        setError('Please select a chart date on the main page first.');
      }
    }
  }, [propSpreadsheetId]);

  // Initialize AudioContext only once
  useEffect(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
        // Create the audio element if it doesn't exist
        if (!audioElementRef.current) {
          audioElementRef.current = new Audio();
        }
      } catch (e) {
        console.error("Web Audio API is not supported in this browser", e);
        setError("Audio processing is not supported in this browser.");
      }
    }

    return () => {
      // Clean up all audio resources when component unmounts
      if (audioElementRef.current) {
        // --- Add Listener Removal --- START ---
        // Remove listeners added dynamically in playAudio to prevent firing during cleanup
        // Create dummy functions to satisfy removeEventListener signature if needed
        const dummyErrorHandler = () => {}; 
        const dummyLoadedMetadataHandler = () => {};
        const dummyEndedHandler = () => {};

        try {
          // Use the same event names as added in playAudio
          audioElementRef.current.removeEventListener('error', dummyErrorHandler); 
          audioElementRef.current.removeEventListener('loadedmetadata', dummyLoadedMetadataHandler);
          audioElementRef.current.removeEventListener('ended', dummyEndedHandler);
          console.log('[VOManager Cleanup] Removed audio element listeners.');
        } catch (removeErr) {
          // Log potential errors during removal but don't block cleanup
          console.warn('[VOManager Cleanup] Error removing audio element listeners:', removeErr); 
        }
        // --- Add Listener Removal --- END ---

        audioElementRef.current.pause();
        audioElementRef.current.src = ''; // Clear src *after* removing listeners
      }
      
      if (sourceNodeRef.current) {
        try {
        sourceNodeRef.current.disconnect();
        } catch (e) { console.warn('[VOManager Cleanup] Error disconnecting source node:', e); }
        sourceNodeRef.current = null;
      }
      
      if (compressorNodeRef.current) {
        try {
        compressorNodeRef.current.disconnect();
        } catch (e) { console.warn('[VOManager Cleanup] Error disconnecting compressor node:', e); }
        compressorNodeRef.current = null;
      }
      
      if (gainNodeRef.current) {
        try {
          gainNodeRef.current.disconnect();
        } catch (e) { console.warn('[VOManager Cleanup] Error disconnecting gain node:', e); }
        // Don't nullify here
      }
      
      // Call setActiveAnalyser(null) on unmount
      setActiveAnalyser(null);

      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        console.log('[VOManager Cleanup] Closing AudioContext');
        audioContextRef.current.close().catch(err => {
          console.error('[VOManager Cleanup] Error closing AudioContext:', err);
        });
        audioContextRef.current = null; // Set ref to null after closing
      }
    };
  }, []); // Changed from [activeAnalyser] to [] for one-time setup & unmount cleanup

  // New useEffect to handle activeAnalyser cleanup
  useEffect(() => {
    // This effect runs when activeAnalyser changes or on unmount.
    // The returned cleanup function will clean up the *previous* analyser.
    let analyserToClean = activeAnalyser; // Capture the current activeAnalyser

    return () => {
      if (analyserToClean) {
        console.log('[VOManager Cleanup] Disconnecting an analyser node due to change or unmount.');
        try {
          analyserToClean.disconnect();
        } catch (e) {
          console.warn('[VOManager Cleanup] Error disconnecting analyser node:', e);
        }
        // We might not want to call setActiveAnalyser(null) here, 
        // as a new one might have just been set. 
        // The component that sets activeAnalyser should handle setting it to null if appropriate.
        // However, if the component is unmounting, activeAnalyser should be nullified by the main effect's setActiveAnalyser(null) call,
        // but that was removed to put it here. Let's reconsider.
        // The main useEffect's cleanup calls setActiveAnalyser(null) - no, that was part of the block I removed.
        // The issue is, when a new analyser is set, this cleanup runs for the *old* one.
        // When the component unmounts, this cleanup also runs.

        // Let's rethink. The original block in the main useEffect was:
        // if (activeAnalyser) {
        //   try { activeAnalyser.disconnect(); } catch (e) { /* ... */ }
        //   setActiveAnalyser(null); 
        // }
        // This `setActiveAnalyser(null)` is important on unmount.

        // Perhaps the new useEffect should only handle disconnect.
        // And the main unmount effect should ensure setActiveAnalyser(null) is called.
        // Let's add setActiveAnalyser(null) back to the main unmount effect's activeAnalyser block if we reinstate it,
        // or ensure it's called on unmount some other way.

        // For now, let's just disconnect. The playAudio function sets the new analyser,
        // and stopAudio sets it to null.
      }
    };
  }, [activeAnalyser]); // This effect depends on activeAnalyser

  // Apply global gain settings when they change
  useEffect(() => {
    const audioContext = audioContextRef.current;
    if (!audioContext) return;
    
    try {
      // Clean up previous gain node if it exists
      if (gainNodeRef.current) {
        gainNodeRef.current.disconnect();
      }
      // The compressorNodeRef is no longer used, ensure it's disconnected if it was somehow set
      if (compressorNodeRef.current) {
        compressorNodeRef.current.disconnect();
        compressorNodeRef.current = null; // Clear the ref
      }
      
      // Create global gain node
      gainNodeRef.current = audioContext.createGain();
      const makeupGain = limiterSettings.gain;
      const linearGain = Math.pow(10, makeupGain / 20); // Convert dB to linear gain
      gainNodeRef.current.gain.value = linearGain;
      
      console.log(`Applied global gain setting: makeupGain=${makeupGain}dB (linear gain: ${linearGain.toFixed(4)})`);
      
      // Also ensure the export settings are synced whenever gain changes.
      // Spread the existing stored settings so per-line gain entries
      // (lineGain_*) are preserved — writing a bare { makeupGain } object here
      // wiped them out (same bug family as the Outro-volume-reverts-to-0 fix).
      if (activeSpreadsheetId) {
        const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
        const existing = JSON.parse(localStorage.getItem(storageKey) || '{}');
        const settingsForExport = { ...existing, makeupGain: String(makeupGain) };
        localStorage.setItem(storageKey, JSON.stringify(settingsForExport));
        console.log(`[VOManager gainSync] Auto-synced export settings for gain change: ${makeupGain}dB to ${storageKey} (preserved line gains)`);
      }
    } catch (err) {
      console.error('Error setting up global gain node:', err);
    }
  }, [limiterSettings, activeSpreadsheetId]);

  // Add effect to ensure export settings have updated global gain while preserving line-specific gains
  useEffect(() => {
    if (activeSpreadsheetId && limiterSettings.gain !== undefined) {
      // Update global gain in export settings while preserving line-specific gains
      const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
      const existing = JSON.parse(localStorage.getItem(storageKey) || '{}');
      const settingsForExport = {
        ...existing, // Preserve lineGain_* entries
        makeupGain: String(limiterSettings.gain)
      };
      localStorage.setItem(storageKey, JSON.stringify(settingsForExport));
      console.log(`[VOManager autoRestore] Updated export settings with global gain: ${limiterSettings.gain}dB, preserved line gains (${storageKey})`);
    }
  }, [activeSpreadsheetId, limiterSettings.gain]);

  const fetchVOData = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');

    if (!activeSpreadsheetId) {
        setError('No spreadsheet ID provided');
        setLoading(false);
      return;
    }

      // Load VO data from the chart-data API (vo_lines). Populates the same state
      // maps the editor consumes (voData/in/out/lineVoUrls/uploaded/gain/titles/
      // artists) plus the week-level processed-audio URL, global gain, and sheetDate.
      const vo = await chartDataService.getVO(activeSpreadsheetId, activeLanguage);
      setVoData(vo.voData || {});
      setVoInPoints(vo.voInPoints || {});
      setVoOutPoints(vo.voOutPoints || {});
      // Set unconditionally (clear-on-empty) so stale per-sheet state from a
      // previous chart/mode doesn't linger.
      setLineAudioFiles(vo.lineVoUrls || {});
      setUploadedAudioFiles(vo.uploadedVoUrls || {});
      setLineGainSettings(vo.lineGain || {});
      setSongTitles(vo.songTitles || {});
      setSongArtists(vo.songArtists || {});
      // Monolithic processed-audio URL (upload mode). Clear when absent.
      // Normalize relative values to absolute.
      if (vo.processedVoUrl) {
        const purl = vo.processedVoUrl.startsWith('http') ? vo.processedVoUrl : `${API_BASE_URL}${vo.processedVoUrl}`;
        setProcessedAudioUrl(purl);
        // S3→local CORS cache so playback isn't blocked on a raw S3 URL.
        try {
          const cacheResp = await fetch(`${SERVER_BASE_URL}/api/cache-s3-audio`, {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ s3Urls: [{ position: 'processedAudio', url: purl }] }),
          });
          if (cacheResp.ok) {
            const cr = await cacheResp.json();
            if (cr.success && cr.localUrls?.processedAudio) {
              setCachedProcessedAudioUrl(`${SERVER_BASE_URL}${cr.localUrls.processedAudio}`);
            }
          }
        } catch (cacheErr) {
          console.warn('[VOManager] failed to cache processed audio', cacheErr);
        }
      } else {
        setProcessedAudioUrl(null);
      }
      // Global VO gain → limiter gain.
      if (vo.globalGain != null && vo.globalGain !== '' && !isNaN(parseFloat(vo.globalGain))) {
        const g = parseFloat(vo.globalGain);
        setLimiterSettings(prev => ({ ...prev, gain: g }));
        localStorage.setItem(`globalGain_${activeSpreadsheetId}`, String(g));
      }
      // sheetDate drives S3 paths for generate/upload; use the week's human
      // date_string, fall back to name.
      if (vo.dateString) setSheetDate(vo.dateString);
      else if (vo.sheetName) setSheetDate(vo.sheetName);
      console.log('[VOManager] Loaded VO data:', Object.keys(vo.voData || {}).length, 'sections');
    } catch (err) {
      console.error('Error fetching VO data:', err);
      setError(`Failed to load VO data: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setLoading(false);
    }
  }, [activeSpreadsheetId, activeLanguage]);

  // When the active sheet changes, adopt that sheet's stored VO language (default
  // 'en'). Keeps the toggle per-week, matching how all VO state is per-sheet. The
  // chart-data interception derives language per-sheet from this same localStorage
  // key, so no separate sync is needed.
  useEffect(() => {
    setActiveLanguageState(getVoLanguage(activeSpreadsheetId));
  }, [activeSpreadsheetId]);

  // Toggle handler: persist per-sheet (localStorage = source of truth for both the
  // explicit getVO call and the generic interception) + flip state so fetchVOData
  // re-runs via its activeLanguage dep.
  const handleSetLanguage = useCallback((lang: VoLanguage) => {
    if (activeSpreadsheetId) setVoLanguage(activeSpreadsheetId, lang);
    setActiveLanguageState(lang);
  }, [activeSpreadsheetId]);

  useEffect(() => {
    if (activeSpreadsheetId) {
        fetchVOData();

        // HYBRID APPROACH: hydrate instantly from localStorage for snappy UI;
        // fetchVOData (above) is the authoritative source and overwrites when it resolves.
        const processedAudioKey = `processedAudioUrl_${activeSpreadsheetId}`;
        const cachedProcessedUrl = localStorage.getItem(processedAudioKey);

        // Only load from cache if it's a local URL (not S3), to avoid CORS errors
        const isS3Url = cachedProcessedUrl && (cachedProcessedUrl.includes('s3.amazonaws.com') || cachedProcessedUrl.includes('s3.us-west-2.amazonaws.com'));
        if (cachedProcessedUrl && !isS3Url) {
          console.log('[VOManager] Loading cached processed audio URL from localStorage (instant):', cachedProcessedUrl);
          setProcessedAudioUrl(cachedProcessedUrl);
        } else if (cachedProcessedUrl) {
          console.log('[VOManager] Cached URL is S3, will wait for re-caching to avoid CORS');
        }

        // Global gain is loaded authoritatively by fetchVOData from the chart-data
        // /vo endpoint; hydrate instantly from localStorage so the UI isn't 0 until
        // that resolves (fetchVOData overwrites when it returns).
        const cachedGain = localStorage.getItem(`globalGain_${activeSpreadsheetId}`);
        if (cachedGain && !isNaN(parseFloat(cachedGain))) {
          const gainValue = parseFloat(cachedGain);
          console.log('[VOManager] Loaded global gain from localStorage:', gainValue);
          setLimiterSettings(prev => ({ ...prev, gain: gainValue }));
        }

        // Line-specific VO URLs are loaded authoritatively by fetchVOData; hydrate
        // instantly from localStorage so cached lines render immediately.
        const lineAudioKey = `lineAudioFiles_${activeSpreadsheetId}`;
        const cachedLineAudio = localStorage.getItem(lineAudioKey);
        if (cachedLineAudio) {
          try {
            const parsed = JSON.parse(cachedLineAudio);
            console.log('[VOManager] Loading cached line-specific VO URLs from localStorage (instant):', parsed);
            setLineAudioFiles(parsed);
          } catch (e) {
            console.error('[VOManager] Error parsing cached line audio:', e);
          }
        }

        // Load in/out points from localStorage
        const inPointsKey = `voInPoints_${activeSpreadsheetId}`;
        const outPointsKey = `voOutPoints_${activeSpreadsheetId}`;
        const storedInPoints = localStorage.getItem(inPointsKey);
        const storedOutPoints = localStorage.getItem(outPointsKey);

        // Note: lineGainSettings is now loaded ONLY from sheet via fetchVOData
        // localStorage is used only as a cache after sheet load, never to override sheet data

        try {
          if (storedInPoints) {
            setVoInPoints(JSON.parse(storedInPoints));
          }
          if (storedOutPoints) {
            setVoOutPoints(JSON.parse(storedOutPoints));
          }
        } catch (e) {
          console.error('Error parsing stored timestamps or gain settings:', e);
        }
    }
  }, [activeSpreadsheetId, fetchVOData]); // Added fetchVOData dependency

  // Standalone cacheS3Files function that can be called from handleVOButtonClick
  const cacheS3Files = async (): Promise<{updatedProcessedAudioUrl?: string, updatedLineAudioFiles?: {[key: string]: string}}> => {
    if (!activeSpreadsheetId) {
      console.log('[VOManager] Skipping cache - missing spreadsheetId');
      return {};
    }

    console.log('[VOManager] Current lineAudioFiles:', lineAudioFiles);
    console.log('[VOManager] Current processedAudioUrl:', processedAudioUrl);

    // Find S3 URLs that need to be cached from lineAudioFiles
    const s3Urls = Object.entries(lineAudioFiles)
      .filter(([_, url]) => url && typeof url === 'string' && (url.includes('s3.amazonaws.com') || url.includes('s3.us-west-2.amazonaws.com')))
      .map(([position, url]) => ({ position, url }));

    // Also check processedAudioUrl if it's an S3 URL
    if (processedAudioUrl && (processedAudioUrl.includes('s3.amazonaws.com') || processedAudioUrl.includes('s3.us-west-2.amazonaws.com'))) {
      s3Urls.push({ position: 'processedAudio', url: processedAudioUrl });
    }

    console.log('[VOManager] Found S3 URLs to cache:', s3Urls);

    if (s3Urls.length === 0) {
      console.log('[VOManager] No S3 URLs found to cache - all URLs appear to be local already');
      return {};
    }

    console.log(`[VOManager] Caching ${s3Urls.length} S3 audio files locally...`);

    try {
      const response = await fetch(`${SERVER_BASE_URL}/api/cache-s3-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ s3Urls }),
      });

      if (!response.ok) {
        throw new Error(`Failed to cache S3 files: ${response.statusText}`);
      }

      const result = await response.json();
      
      if (result.success && result.localUrls) {
        console.log('[VOManager] Successfully cached S3 files:', result.localUrls);
        
        // Update lineAudioFiles and processedAudioUrl with local URLs
        const updatedLineAudioFiles = { ...lineAudioFiles };
        let updatedProcessedAudioUrl = processedAudioUrl;
        let hasUpdates = false;
        
        Object.entries(result.localUrls).forEach(([position, localUrl]) => {
          if (typeof localUrl === 'string') {
            // Decode the URL to handle %2F -> / conversions
            const decodedUrl = decodeURIComponent(localUrl);
            const fullLocalUrl = decodedUrl.startsWith('http') 
              ? decodedUrl 
              : `${SERVER_BASE_URL}${decodedUrl}`;
            
            if (position === 'processedAudio') {
              // Update processedAudioUrl
              setProcessedAudioUrl(fullLocalUrl);
              updatedProcessedAudioUrl = fullLocalUrl;
              console.log('[VOManager] Updated processedAudioUrl with local URL:', fullLocalUrl);
              hasUpdates = true;
            } else if (updatedLineAudioFiles[position]) {
              // Update lineAudioFiles
              updatedLineAudioFiles[position] = fullLocalUrl;
              hasUpdates = true;
            }
          }
        });

        if (hasUpdates && Object.keys(updatedLineAudioFiles).length > 0) {
          setLineAudioFiles(updatedLineAudioFiles);
          
          // Update localStorage with local URLs
          const lineAudioStorage = JSON.stringify(updatedLineAudioFiles);
          localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, lineAudioStorage);
          console.log('[VOManager] Updated lineAudioFiles with local URLs');
        }
        
        // Return the updated URLs
        return {
          updatedProcessedAudioUrl: updatedProcessedAudioUrl || undefined,
          updatedLineAudioFiles
        };
      }

      if (result.errors && result.errors.length > 0) {
        console.warn('[VOManager] Some S3 files failed to cache:', result.errors);
      }

    } catch (error) {
      console.error('[VOManager] Error caching S3 files:', error);
    }
    
    return {};
  };

  // State for cached local URLs (separate from lineAudioFiles for exports)
  const [cachedLocalUrls, setCachedLocalUrls] = useState<{[key: string]: string}>({});
  // State for cached processedAudioUrl
  const [cachedProcessedAudioUrl, setCachedProcessedAudioUrl] = useState<string | null>(null);
  // State to track if caching is complete - start as true since we might not need to cache
  const [isCachingComplete, setIsCachingComplete] = useState<boolean>(true);
  // Ref to track if we've already shown the caching message (to avoid looping) - using ref instead of state to avoid closure issues
  const hasShownCachingMessageRef = useRef<boolean>(false);

  // Cache S3 audio files locally when lineAudioFiles changes (but keep S3 URLs in lineAudioFiles)
  useEffect(() => {
    const cacheS3Files = async () => {
      console.log('[VOManager] Cache S3 files effect triggered');
      
      if (!activeSpreadsheetId || Object.keys(lineAudioFiles).length === 0) {
        console.log('[VOManager] Skipping lineAudioFiles cache - missing spreadsheetId or empty lineAudioFiles:', { 
          activeSpreadsheetId, 
          lineAudioFilesCount: Object.keys(lineAudioFiles).length,
          lineAudioFiles
        });
        setIsCachingComplete(true); // No caching needed, mark as complete
        return;
      }

      console.log('[VOManager] Current lineAudioFiles (keeping S3 URLs for exports):', lineAudioFiles);

      // Find S3 URLs that need to be cached
      const s3Urls = Object.entries(lineAudioFiles)
        .filter(([_, url]) => url && typeof url === 'string' && (url.includes('s3.amazonaws.com') || url.includes('s3.us-west-2.amazonaws.com')))
        .map(([position, url]) => ({ position, url }));

      console.log('[VOManager] Found S3 URLs to cache:', s3Urls);

      if (s3Urls.length === 0) {
        console.log('[VOManager] No S3 URLs found to cache');
        setIsCachingComplete(true); // No URLs to cache, mark as complete
        return;
      }

      console.log(`[VOManager] Caching ${s3Urls.length} S3 audio files locally for browser playback...`);
      setIsCachingComplete(false); // Start caching ONLY if we have S3 URLs to cache

      try {
        const response = await fetch(`${SERVER_BASE_URL}/api/cache-s3-audio`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ s3Urls }),
        });

        if (!response.ok) {
          throw new Error(`Failed to cache S3 files: ${response.statusText}`);
        }

        const result = await response.json();
        
        if (result.success && result.localUrls) {
          console.log('[VOManager] Successfully cached S3 files for browser playback:', result.localUrls);
          
          // Store cached URLs separately (don't overwrite lineAudioFiles)
          const newCachedUrls = { ...cachedLocalUrls };
          let hasUpdates = false;
          
          Object.entries(result.localUrls).forEach(([position, localUrl]) => {
            if (typeof localUrl === 'string') {
              const fullLocalUrl = localUrl.startsWith('http') 
                ? localUrl 
                : `${SERVER_BASE_URL}${localUrl}`;
              newCachedUrls[position] = fullLocalUrl;
              hasUpdates = true;
            }
          });

          if (hasUpdates) {
            setCachedLocalUrls(newCachedUrls);
            console.log('[VOManager] Updated cached local URLs for browser playback (lineAudioFiles unchanged for exports)');
          }
        }

        if (result.errors && result.errors.length > 0) {
          console.warn('[VOManager] Some S3 files failed to cache:', result.errors);
        }

        setIsCachingComplete(true); // Mark caching as complete

        // Show success message if user tried to play during caching
        if (hasShownCachingMessageRef.current) {
          enqueueSnackbar('Audio loaded and ready to play', {
            variant: 'success',
            autoHideDuration: 2000
          });
        }

      } catch (error) {
        console.error('[VOManager] Error caching S3 files:', error);
        setIsCachingComplete(true); // Mark caching as complete even on error to prevent infinite waiting
      }
    };

    cacheS3Files();
  }, [lineAudioFiles, activeSpreadsheetId]);

  // NOTE: Processed audio caching is now handled in the mount effect (lines 970-1045)
  // This old effect has been removed to avoid duplicate caching logic

  // Check for existing generated audio files in S3 when in generate mode
  useEffect(() => {
    const checkExistingGeneratedAudio = async () => {
      // Only check in generate mode when we have the necessary context
      if (!isGenerateMode || !sheetDate || !currentChartConfig?.voSubfolder) {
        return;
      }

      console.log('[VOManager] Checking for existing generated audio in S3...');
      console.log('[VOManager] voSubfolder:', currentChartConfig.voSubfolder, 'sheetDate:', sheetDate);

      try {
        const response = await fetch(
          `${SERVER_BASE_URL}/api/check-generated-audio?voSubfolder=${encodeURIComponent(currentChartConfig.voSubfolder)}&sheetDate=${encodeURIComponent(sheetDate)}&language=${encodeURIComponent(activeLanguage)}`
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();

        if (result.success && result.generatedAudio) {
          const existingKeys = Object.keys(result.generatedAudio);
          if (existingKeys.length > 0) {
            console.log('[VOManager] Found existing generated audio files:', existingKeys);
            setGeneratedAudio(prev => ({ ...prev, ...result.generatedAudio }));
          } else {
            console.log('[VOManager] No existing generated audio files found in S3');
          }
        }
      } catch (err) {
        console.error('[VOManager] Error checking for existing generated audio:', err);
        // Non-fatal error, just log it
      }
    };

    checkExistingGeneratedAudio();
  }, [isGenerateMode, sheetDate, currentChartConfig?.voSubfolder, activeLanguage]);

  const handleCellClick = (key: string) => {
    if (isGeneratingAll || !activeSpreadsheetId) return;
    // If this is the Outro field and it's empty, populate with the default text
    if (key === 'Outro' && (!voData[key] || voData[key].trim() === '')) {
      setEditingCell({ key, value: getDefaultOutroText(currentChartConfig?.defaultOutroText || '') });
    } else {
      setEditingCell({ key, value: voData[key] || '' });
    }
  };

  const handleCellEditChange = (key: string, value: string) => {
    // Remove the special handling for Outro here
      setEditingCell({ key, value });
  };

  const handleSave = async (key: string, value: string) => {
    if (!key || !activeSpreadsheetId) {
      setError('Cannot save: No spreadsheet selected.')
      return;
    }

    // CELL_MAP gates which keys are editable VO script (column L = scriptText).
    if (!CELL_MAP[key]) {
      setError(`Invalid key: ${key}`);
      setEditingCell(null);
      return;
    }

    console.log(`Saving "${value}" to ${activeSpreadsheetId}, section: ${keyToSection(key)} scriptText`);

    const previousValue = voData[key];
    setVoData((prev) => ({ ...prev, [key]: value }));
    setEditingCell(null);

    // A VO line with no script text has no voice-over, so it can't have an
    // in/out point — a lingering in/out is stale (e.g. text was cleared after a
    // prior alignment) and the export would slice an empty clip from it. When
    // the text is emptied, blank this section's in/out too so the data stays
    // self-consistent. (Tease is the common case: it was historically excluded
    // from the bulk VO clear, so its in/out could survive the text going blank.)
    const clearedText = !value || value.trim() === '';

    try {
      await chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'scriptText', value, activeLanguage);
      if (clearedText) {
        await Promise.all([
          chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'inPoint', '', activeLanguage),
          chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'outPoint', '', activeLanguage),
        ]);
        // Mirror the clear into local state + localStorage so the UI and the
        // export payload (built from these) don't keep showing the stale points.
        setVoInPoints((prev) => {
          const next = { ...prev }; delete next[key];
          if (activeSpreadsheetId) localStorage.setItem(`voInPoints_${activeSpreadsheetId}`, JSON.stringify(next));
          return next;
        });
        setVoOutPoints((prev) => {
          const next = { ...prev }; delete next[key];
          if (activeSpreadsheetId) localStorage.setItem(`voOutPoints_${activeSpreadsheetId}`, JSON.stringify(next));
          return next;
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error saving data:', err);
      setError(err instanceof Error ? err.message : 'Failed to save data');
      setVoData((prev) => ({ ...prev, [key]: previousValue }));
    }
  };

  const playAudio = useCallback((key: string, overrideProcessedAudioUrl?: string) => {
    try {
      // Wait for caching to complete before playing
      if (!isCachingComplete) {
        console.log('[VOManager] Waiting for audio caching to complete before playback...');
        // Only show the message once using ref to avoid closure issues
        if (!hasShownCachingMessageRef.current) {
          enqueueSnackbar('Loading audio from cloud storage, please wait...', {
            variant: 'info',
            autoHideDuration: 3000 // Show for 3 seconds then auto-dismiss
          });
          hasShownCachingMessageRef.current = true; // Never reset this flag - only show once per session
        }
        // Retry after a short delay
        setTimeout(() => playAudio(key, overrideProcessedAudioUrl), 500);
        return;
      }

      // For generate mode, we use generated audio
      // For upload mode, we play a segment from the processed audio
      let audioSource = '';
      
      // An uploaded/override clip for this line (cached locally or in column S).
      // If present, it must take precedence over the default-outro fallback —
      // otherwise pressing play on an outro with an uploaded override would
      // silently play the default instead.
      const hasLineOverride = !!(cachedLocalUrls[key] || (lineAudioFiles[key] && typeof lineAudioFiles[key] === 'string'));

      if (isGenerateMode) {
        audioSource = generatedAudio[key] || '';
      } else if (key === 'Outro' && !hasLineOverride && (
        // Use default outro audio if script field is empty OR both in/out points are 0
        (!voData[key] || voData[key].trim() === '') ||
        (voInPoints[key] === '0' && voOutPoints[key] === '0')
      )) {
        // Play default Outro audio when script is empty OR in/out points are both 0.
        // Cache-bust so an updated default-outro.wav (same S3 key) is always re-fetched
        // rather than served from a stale browser cache.
        console.log('[VOManager] Playing default Outro audio because script field is empty or in/out points are both 0.');
        audioSource = `${getDefaultOutroAudioUrl(currentChartConfig?.voSubfolder || 'hot-100')}?cb=${Date.now()}`;
      } else {
        // For other keys in upload mode, OR for Outro with a non-empty script,
        // check line-specific or main URL (prefer cached URLs for browser playback)
        if (cachedLocalUrls[key]) {
          audioSource = cachedLocalUrls[key];
          console.log(`Using cached line-specific audio for CORS-free playback: ${cachedLocalUrls[key]}`);
        } else if (lineAudioFiles[key] && typeof lineAudioFiles[key] === 'string') {
          // Check if it's an S3 URL that needs caching
          if (lineAudioFiles[key].includes('s3.amazonaws.com') || lineAudioFiles[key].includes('s3.us-west-2.amazonaws.com')) {
            console.warn(`Cannot play S3 URL directly due to CORS for line ${key}. Waiting for cache...`);
            setError('Audio is being loaded from cloud storage. Please wait a moment and try again.');
            return;
          }
          audioSource = lineAudioFiles[key];
          console.log(`Using line-specific audio: ${lineAudioFiles[key]}`);
        } else if (overrideProcessedAudioUrl) {
          audioSource = overrideProcessedAudioUrl;
          console.log(`Using override processedAudioUrl: ${overrideProcessedAudioUrl}`);
        } else if (cachedProcessedAudioUrl) {
          audioSource = cachedProcessedAudioUrl;
          console.log(`Using cached processedAudioUrl for CORS-free playback: ${cachedProcessedAudioUrl}`);
        } else if (processedAudioUrl) {
          // Check if it's an S3 URL that needs caching
          if (processedAudioUrl.includes('s3.amazonaws.com') || processedAudioUrl.includes('s3.us-west-2.amazonaws.com')) {
            console.warn(`Cannot play S3 URL directly due to CORS. Waiting for cache...`);
            setError('Audio is being loaded from cloud storage. Please wait a moment and try again.');
            return;
          }
          audioSource = processedAudioUrl;
          console.log(`Using processedAudioUrl: ${processedAudioUrl}`);
        } else {
          throw new Error('No audio source available');
        }
      }

      if (!audioSource || typeof audioSource !== 'string') {
        console.error(`[VOManager] Invalid audio source for ${key}:`, audioSource, `Type: ${typeof audioSource}`);
        throw new Error(`No valid audio source available for ${key} (got ${typeof audioSource})`);
      }
    
      // === RAILWAY DEBUGGING: Comprehensive audio source analysis ===
      console.log(`[AUDIO DEBUG] ===== STARTING PLAYBACK FOR ${key} =====`);
      console.log(`[AUDIO DEBUG] Raw audioSource: "${audioSource}"`);
      console.log(`[AUDIO DEBUG] URL type: ${typeof audioSource === 'string' ? (audioSource.includes('s3.amazonaws.com') ? 'S3' : audioSource.includes('railway.app') ? 'Railway' : 'Other') : 'Non-string'}`);
      console.log(`[AUDIO DEBUG] Environment: ${window.location.hostname}`);
      console.log(`[AUDIO DEBUG] User agent: ${navigator.userAgent.substring(0, 50)}...`);

      // Test basic URL accessibility
      try {
        const testUrl = new URL(audioSource);
        console.log(`[AUDIO DEBUG] URL parsed successfully - Host: ${testUrl.hostname}, Path: ${testUrl.pathname}`);
      } catch (err) {
        console.error(`[AUDIO DEBUG] ❌ Invalid URL format:`, err);
      }

      // Always create a new audio element to avoid reconnection issues
      const audioElement = new Audio();
    
      // Add error handling before setting source to catch CORS and other load errors
      audioElement.addEventListener('error', (event) => {
        console.error(`[AUDIO DEBUG] ❌ Audio element error for ${key}:`, event);
        console.error(`[AUDIO DEBUG] ❌ Audio element src: "${audioElement.src}"`);
        console.error(`[AUDIO DEBUG] ❌ Audio element crossOrigin: "${audioElement.crossOrigin}"`);
        console.error(`[AUDIO DEBUG] ❌ Audio element readyState: ${audioElement.readyState}`);
        console.error(`[AUDIO DEBUG] ❌ Audio element networkState: ${audioElement.networkState}`);
        console.error(`[AUDIO DEBUG] ❌ Error details:`, {
          target: event.target,
          type: event.type,
          timeStamp: event.timeStamp,
          bubbles: event.bubbles,
          cancelable: event.cancelable
        });

        // Try to identify specific error type
        if (audioElement.error) {
          const errorTypes: Record<number, string> = {
            1: 'MEDIA_ERR_ABORTED - Fetching aborted by user',
            2: 'MEDIA_ERR_NETWORK - Network error occurred',
            3: 'MEDIA_ERR_DECODE - Error occurred during decoding',
            4: 'MEDIA_ERR_SRC_NOT_SUPPORTED - Audio format not supported'
          };
          console.error(`[AUDIO DEBUG] ❌ Media error code: ${audioElement.error.code} - ${errorTypes[audioElement.error.code] || 'Unknown error'}`);
          console.error(`[AUDIO DEBUG] ❌ Media error message: "${audioElement.error.message}"`);
        }

        setPlayingKey(null);
    
        // Show error toast with more specific message
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
          const errorMessage = (typeof audioSource === 'string' && audioSource.includes('s3.amazonaws.com'))
            ? 'Audio file needs to be cached from cloud storage. Please refresh the page and try again.'
            : 'Unable to load audio file. The file may not exist or the format is not supported.';
          enqueueSnackbar(`Audio playback error: ${errorMessage}`, {
            variant: 'error',
            autoHideDuration: 5000
          });
        } else {
          console.log('[VOManager] Suppressing error notification - component likely unmounted');
        }
        
        // Clean up any existing audio processing
      if (sourceNodeRef.current) {
        try {
          sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
        } catch (err) {
            console.error('Error disconnecting source node:', err);
        }
        }
      }, { once: true });

      // Set audio properties and source with comprehensive debugging
      console.log(`[AUDIO DEBUG] ===== CROSSORIGIN DECISION =====`);

      // Check if the URL is from a different origin (including different ports)
      const audioUrl = new URL(audioSource, window.location.origin);
      const currentUrl = new URL(window.location.href);
      const isDifferentOrigin = audioUrl.origin !== currentUrl.origin;

      console.log(`[AUDIO DEBUG] Audio URL origin: ${audioUrl.origin}`);
      console.log(`[AUDIO DEBUG] Current page origin: ${currentUrl.origin}`);
      console.log(`[AUDIO DEBUG] Different origin: ${isDifferentOrigin}`);

      // Always set crossOrigin for different origins (including different ports on localhost)
      if (isDifferentOrigin) {
        audioElement.crossOrigin = "anonymous";
        console.log(`[AUDIO DEBUG] ✅ Set crossOrigin="anonymous" for different origin`);
      } else {
        audioElement.removeAttribute('crossorigin');
        console.log(`[AUDIO DEBUG] ✅ Removed crossOrigin for same origin`);
      }

      console.log(`[AUDIO DEBUG] Final crossOrigin value: "${audioElement.crossOrigin}"`);
      console.log(`[AUDIO DEBUG] Setting audio src to: "${audioSource}"`);

      audioElement.src = audioSource;
      // Add success event handlers for debugging
      audioElement.addEventListener('loadstart', () => {
        console.log(`[AUDIO DEBUG] ✅ loadstart event - Starting to load ${key}`);
      });

      audioElement.addEventListener('loadedmetadata', () => {
        console.log(`[AUDIO DEBUG] ✅ loadedmetadata event - Metadata loaded for ${key}`);
        console.log(`[AUDIO DEBUG] ✅ Duration: ${audioElement.duration}s`);
      });

      audioElement.addEventListener('canplay', () => {
        console.log(`[AUDIO DEBUG] ✅ canplay event - Audio ready to play ${key}`);
      });

      audioElement.addEventListener('canplaythrough', () => {
        console.log(`[AUDIO DEBUG] ✅ canplaythrough event - Audio fully buffered ${key}`);
      });

      console.log(`[AUDIO DEBUG] Audio element configured successfully`);
      audioElementRef.current = audioElement;

      // Create or get audio context
      let audioContext = audioContextRef.current;
      if (!audioContext || audioContext.state === 'closed') {
        try {
          audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
          audioContextRef.current = audioContext;
        } catch (error) {
          console.error('Failed to create AudioContext:', error);
          throw new Error('Could not create audio context. Please check your browser settings.');
      }
      } else if (audioContext.state === 'suspended') {
        audioContext.resume().catch((error) => {
          console.error('Failed to resume AudioContext:', error);
          throw new Error('Could not resume audio context. Please check your browser settings.');
        });
      }
      
      setPlayingKey(key);
      
      // Set up audio context and nodes first, then handle playback timing
      try {
        console.log(`[DEBUG] Setting up audio context and nodes...`);
        // Make sure audioContext is not null before proceeding
        if (!audioContext) {
          throw new Error('AudioContext is not available');
        }

        // Create a new source node from the audio element
        const sourceNode = audioContext.createMediaElementSource(audioElement);
        sourceNodeRef.current = sourceNode; // Keep track of the source

        // Create a new AnalyserNode for this playback instance
        const newAnalyser = audioContext.createAnalyser();
        newAnalyser.fftSize = 256; // Or your preferred fftSize
        
        // --- START Gain Adjustment (Line-specific overrides Global) ---
        const hasLineSpecificGain = lineGainSettings[key] !== undefined;
        console.log(`[VOManager] Current limiterSettings.gain value: ${limiterSettings.gain}`);
        console.log(`[VOManager] localStorage globalGain: ${localStorage.getItem(`globalGain_${activeSpreadsheetId}`)}`);
        console.log(`[VOManager] lineGainSettings[${key}]: ${lineGainSettings[key]}`);
        console.log(`[VOManager] hasLineSpecificGain: ${hasLineSpecificGain}`);

        // Special case for Outro: default to 0dB if using default VO (no VO text, no line audio) and P13 is not set
        let gainDb;
        if (key === 'Outro' && (!voData[key] || voData[key].trim() === '') && !lineAudioFiles[key] && !hasLineSpecificGain) {
          gainDb = 0; // Default Outro (no text, no line audio, no line gain) uses 0dB
          console.log(`[VOManager] Using default Outro with 0dB gain (no VO text, no line audio, no line gain override)`);
        } else {
          gainDb = hasLineSpecificGain ? lineGainSettings[key] : limiterSettings.gain;
        }
        console.log(`[VOManager] Final gainDb to use: ${gainDb} (using ${hasLineSpecificGain ? 'line-specific' : key === 'Outro' && (!voData[key] || voData[key].trim() === '') ? 'default outro' : 'global'} gain)`);
        const linearGain = Math.pow(10, gainDb / 20);
        console.log(`[VOManager] Linear gain value: ${linearGain}`);
        const gainNode = audioContext.createGain();
        gainNode.gain.value = linearGain;
        console.log(`[VOManager] GainNode created with value: ${gainNode.gain.value}`);


        if (hasLineSpecificGain) {
          console.log(`[VOManager] Applying line-specific gain for ${key}: ${gainDb}dB (linear: ${linearGain.toFixed(4)}) - overriding global gain`);
        } else {
          console.log(`[VOManager] Applying global gain for ${key}: ${gainDb}dB (linear: ${linearGain.toFixed(4)}) - no line-specific override`);
        }

        // Connect source to gain node (either line-specific or global)
        sourceNode.connect(gainNode);
        let currentNode: AudioNode = gainNode;
        console.log('[VOManager] Audio routing: source -> gain (line-specific OR global)');
        
        // Now, connect from global gain (or line gain if global is missing) to analyser
        // Compressor stage is removed.
        console.log('[VOManager] Audio routing: ... -> analyser -> destination (compressor removed)');
        currentNode.connect(newAnalyser);
        newAnalyser.connect(audioContext.destination);

        // Update the ref *before* setting playingKey to ensure VUMeter gets the new analyser
        setActiveAnalyser(newAnalyser);
        
        // Define the playback function that handles timing
        const startPlayback = () => {
          console.log(`[DEBUG] Timing check - key: ${key}, isGenerateMode: ${isGenerateMode}`);
          console.log(`[DEBUG] audioSource: ${audioSource}`);
          console.log(`[DEBUG] processedAudioUrl: ${processedAudioUrl}`);
          console.log(`[DEBUG] lineAudioFiles[${key}]: ${lineAudioFiles[key]}`);
          console.log(`[DEBUG] audioSource === processedAudioUrl: ${audioSource === processedAudioUrl}`);
          console.log(`[DEBUG] lineAudioFiles[key] exists: ${!!lineAudioFiles[key]}`);
          
          // For upload mode with any audio file, we need to set specific time segments
          // Check if audioSource matches any uploaded audio file (all need in/out points)
          const isUploadedAudioFile = audioSource === processedAudioUrl ||
                                      audioSource === cachedProcessedAudioUrl ||
                                      audioSource === lineAudioFiles[key] ||
                                      audioSource === cachedLocalUrls[key];

          console.log(`[DEBUG] cachedLocalUrls[${key}]: ${cachedLocalUrls[key]}`);
          console.log(`[DEBUG] isUploadedAudioFile: ${isUploadedAudioFile}`);
          console.log(`[DEBUG] audioSource === cachedLocalUrls[key]: ${audioSource === cachedLocalUrls[key]}`);

          // Apply in/out points for ALL uploaded audio files (including line-specific from Column S)
          // Also apply for generated audio if in/out points exist
          // Exception: Skip in/out points only when Outro is using the default outro clip
          // Compare without any cache-busting query string (audioSource may carry ?cb=...).
          const isDefaultOutro = key === 'Outro' &&
            audioSource.split('?')[0] === getDefaultOutroAudioUrl(currentChartConfig?.voSubfolder || 'hot-100');
          const hasInOutPoints = voInPoints[key] && voOutPoints[key] &&
            !(voInPoints[key] === '0' && voOutPoints[key] === '0'); // Ignore if both are 0 (default)

          // For generate mode: apply in/out points if they exist
          // For upload mode: apply for uploaded files (skip only default outro clip)
          const shouldApplyInOutPoints = isGenerateMode
            ? hasInOutPoints // Generated audio: apply if points exist
            : (isUploadedAudioFile && !isDefaultOutro); // Upload mode: apply unless default outro

          console.log(`[DEBUG] shouldApplyInOutPoints: ${shouldApplyInOutPoints}`);
          console.log(`[DEBUG] Condition breakdown - isGenerateMode: ${isGenerateMode}, hasInOutPoints: ${hasInOutPoints}, isUploadedAudioFile: ${isUploadedAudioFile}`);

          if (shouldApplyInOutPoints) {
            const inPoint = voInPoints[key];
            const outPoint = voOutPoints[key];
            
            console.log(`[DEBUG] inPoint: ${inPoint}, outPoint: ${outPoint}`);
            console.log(`[DEBUG] voInPoints:`, voInPoints);
            console.log(`[DEBUG] voOutPoints:`, voOutPoints);
            
            if (inPoint && outPoint) {
              const inSeconds = parseFloat(inPoint);
              const outSeconds = parseFloat(outPoint);
              
              console.log(`Setting time range: ${inSeconds}s to ${outSeconds}s`);
              
              // Function to actually play with timing
              const playWithTiming = () => {
                console.log(`Setting up timed playback: ${inSeconds}s to ${outSeconds}s, readyState: ${audioElement.readyState}`);

                // Set the start time BEFORE playing to avoid hearing the beginning
                console.log(`Setting currentTime to ${inSeconds}s before playback`);
                audioElement.currentTime = inSeconds;

                let timeoutId: NodeJS.Timeout | null = null;

                // Set up the duration-based stop
                const durationSeconds = outSeconds - inSeconds;
                if (durationSeconds > 0) {
                  timeoutId = setTimeout(() => {
                    console.log(`Stopping playback at ${outSeconds}s`);
                    audioElement.pause();
                    setPlayingKey(null);
                  }, durationSeconds * 1000);
                }

                // Cleanup function for timeout
                const cleanup = () => {
                  if (timeoutId) {
                    clearTimeout(timeoutId);
                  }
                };

                // Clean up when audio ends or stops
                audioElement.addEventListener('pause', cleanup, { once: true });
                audioElement.addEventListener('ended', cleanup, { once: true });

                // Start playing
                audioElement.play()
                  .catch(error => {
                    cleanup();
                    console.error('Error during audio playback:', error);
                    enqueueSnackbar(`Playback error: ${error.message}`, { 
                      variant: 'error',
                      autoHideDuration: 3000
                    });
                    setPlayingKey(null);
                  });
              };
              
              // Wait for metadata to be loaded before seeking
              if (audioElement.readyState >= 1) { // HAVE_METADATA or better
                playWithTiming();
              } else {
                audioElement.addEventListener('loadedmetadata', playWithTiming, { once: true });
              }
              return; // Don't execute the fallback play below
            }
          } else {
            console.log(`[DEBUG] No in/out points applied. isGenerateMode: ${isGenerateMode}, isUploadedAudioFile: ${isUploadedAudioFile}, key: ${key}`);
          }

          // Fallback: Play without timing for generated audio or when no in/out points
          audioElement.play()
            .catch(error => {
              console.error('Error during audio playback:', error);
              enqueueSnackbar(`Playback error: ${error.message}`, { 
                variant: 'error',
                autoHideDuration: 3000
              });
              setPlayingKey(null);
            });
        };

        console.log(`[DEBUG] About to check audio readyState...`);
        // Check if audio is ready to play, otherwise wait for canplay event
        console.log(`[DEBUG] Audio readyState: ${audioElement.readyState}, checking if >= 3`);
        if (audioElement.readyState >= 3) { // HAVE_FUTURE_DATA or better
          console.log(`[DEBUG] Audio ready, calling startPlayback()`);
          startPlayback();
        } else {
          console.log(`[DEBUG] Audio not ready, waiting for canplay event`);
          audioElement.addEventListener('canplay', startPlayback, { once: true });
        }
        
      } catch (error) {
        console.error('Error setting up audio nodes:', error);
        enqueueSnackbar(`Audio setup error: ${error instanceof Error ? error.message : 'Unknown error'}`, { 
          variant: 'error',
          autoHideDuration: 3000
        });
        setPlayingKey(null);
      }
      
      // Handle audio ending
      audioElement.addEventListener('ended', () => {
        setPlayingKey(null);
      }, { once: true });
      
    } catch (error) {
      console.error('Playback error:', error);
      enqueueSnackbar(`Playback error: ${error instanceof Error ? error.message : 'Unknown error'}`, { 
        variant: 'error',
        autoHideDuration: 3000
      });
    }
  }, [
    isCachingComplete,
    cachedLocalUrls,
    cachedProcessedAudioUrl,
    isGenerateMode,
    generatedAudio,
    processedAudioUrl,
    lineAudioFiles,
    voInPoints,
    voOutPoints,
    enqueueSnackbar,
    voData,
    setActiveAnalyser,
    sourceNodeRef,
    gainNodeRef,
    lineGainSettings,
    limiterSettings
  ]);

  const stopAudio = useCallback(() => {
    // Stop any currently playing audio
    if (audioElementRef.current) {
      audioElementRef.current.pause();
    }
    // It's also good practice to disconnect the analyser when stopping
    if (activeAnalyser) { // Check activeAnalyser state
      activeAnalyser.disconnect();
      setActiveAnalyser(null); // Clear it
    }
    setPlayingKey(null);
  }, [setPlayingKey, activeAnalyser, setActiveAnalyser]); // Added activeAnalyser and setActiveAnalyser based on new linter warning (and prior removal reasoning)

  const handleVOButtonClick = (key: string) => {
    if (isGeneratingAll) return;

    if (playingKey === key) {
      stopAudio();
    } else {
      if (playingKey) {
        stopAudio();
      }

      const audioSource = isGenerateMode
        ? generatedAudio[key]
        : (lineAudioFiles[key] || cachedLocalUrls[key] || processedAudioUrl || cachedProcessedAudioUrl);

      if (audioSource) {
        if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
          audioContextRef.current.resume()
            .then(() => playAudio(key))
            .catch(err => {
              console.error('Error resuming AudioContext:', err);
              setError(`Error resuming audio: ${err instanceof Error ? err.message : String(err)}`);
            });
        } else {
          playAudio(key);
        }
      } else {
        setError(isGenerateMode ? `No generated audio found for "${key}"` : 'No processed audio file available');
      }
    }
  };

  const handleGenerateAll = async () => {
    if (!activeSpreadsheetId || isGeneratingAll) return;
    setIsGeneratingAll(true);
    setError(null);

    try {
      // Generate audio for all entries that have script text
      const keysToGenerate = DISPLAY_ORDER.filter(key => voData[key]?.trim());
      console.log(`[VOManager] Generate All: Found ${keysToGenerate.length} lines with script text:`, keysToGenerate);

      if (keysToGenerate.length === 0) {
        enqueueSnackbar('No script text found. Please add script text first.', { variant: 'warning' });
        return;
      }

      let successCount = 0;
      for (const key of keysToGenerate) {
        try {
          console.log(`[VOManager] Generating audio for ${key}...`);
          await generateAudio(key, voData[key]);
          successCount++;
        } catch (err) {
          console.error(`[VOManager] Failed to generate audio for ${key}:`, err);
          // Continue with other keys even if one fails
        }
      }

      if (successCount > 0) {
        enqueueSnackbar(`Generated audio for ${successCount} lines`, { variant: 'success' });
      }
    } catch (err) {
      console.error('Error generating all audio:', err);
      setError(`Error generating all audio: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsGeneratingAll(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Capture reference to input element before async operations (React synthetic event gets nullified)
    const inputElement = event.target;

    // Check if Intro and positions 1-10 have VO script text before allowing upload
    const requiredPositions = ['Intro', '10', '9', '8', '7', '6', '5', '4', '3', '2', '1'];
    const missingScripts = requiredPositions.filter(pos => !voData[pos] || voData[pos].trim() === '');

    if (missingScripts.length > 0) {
      enqueueSnackbar('Upload VO script text first, then upload VO audio.', { variant: 'warning' });
      // Reset the file input so the same file can be selected again after adding scripts
      event.target.value = '';
      return;
    }

    setIsProcessing(true);
    setError(null);
    setUploadProgress(0);

    try {
      // Process audio - converts to WAV but without volume adjustments (ATSC normalization temporarily disabled)
      const processedBlob = await processAudioWithATSCNormalization(file);

      const formData = new FormData();
      formData.append('audio', processedBlob, file.name);
      
      // Add script data to the request
      formData.append('scriptData', JSON.stringify(voData));

      // Add sheetDate for S3 folder structure (for S3 upload after processing)
      if (sheetDate) {
        formData.append('sheetDate', sheetDate);
        console.log(`[VOManager] Uploading processed file: ${file.name} with sheetDate: ${sheetDate}`);
      } else {
        console.log(`[VOManager] Uploading processed file: ${file.name} (no sheetDate - will use local storage)`);
      }
      // Send the chart's selected language so STT transcribes with the right model
      // (the server maps it via voLanguageToSttCode, defaulting to en-US). This is no
      // longer auto-detected from the script. For EN the S3 prefix is segment-less, so
      // EN paths stay byte-identical; ES correctly gets the /es/ segment.
      formData.append('language', activeLanguage || 'en');
      // NOTE: still does NOT send voSubfolder — the monolithic result URL is stored in
      // the processed-VO field and replayed by the export, so its S3 path isn't
      // reconstructed from voSubfolder. Per-line bilingual VO uses process-line-audio.

      // Start the STT job. The server processes in the background (~80s) and
      // returns a jobId immediately, so the upload connection isn't held open
      // long enough to hit the Railway edge proxy timeout (which previously
      // reset the connection and left the Outro/timestamps unsaved).
      const startResponse = await fetch(`${API_BASE_URL}/api/process-stt-gemini`, {
        method: 'POST',
        body: formData
      });

      if (!startResponse.ok) {
        throw new Error(`HTTP error! status: ${startResponse.status}`);
      }

      const { jobId } = await startResponse.json();
      if (!jobId) {
        throw new Error('Server did not return a job id for STT processing');
      }
      console.log(`[VOManager] STT job started: ${jobId}. Polling for completion...`);

      // Poll until the job is done or errors. ~80s typical; cap at ~5 min.
      const POLL_INTERVAL_MS = 3000;
      const MAX_POLL_MS = 5 * 60 * 1000;
      const pollStart = Date.now();
      let result: TranscriptionResult | null = null;
      while (true) {
        if (Date.now() - pollStart > MAX_POLL_MS) {
          throw new Error('STT processing timed out (no result after 5 minutes)');
        }
        await new Promise(resolve => setTimeout(resolve, POLL_INTERVAL_MS));
        const statusResponse = await fetch(`${API_BASE_URL}/api/process-stt-gemini/status/${jobId}`);
        if (!statusResponse.ok && statusResponse.status !== 500) {
          // 404 (expired/unknown) or other non-result error
          throw new Error(`STT status check failed: ${statusResponse.status}`);
        }
        const statusData = await statusResponse.json();
        if (statusData.status === 'processing') {
          continue;
        }
        if (statusData.status === 'done') {
          result = statusData.result as TranscriptionResult;
          break;
        }
        // error
        throw new Error(statusData.error || statusData.details || 'STT processing failed');
      }
      console.log(`[VOManager] STT job ${jobId} complete.`);

      if (result.fileUrl) {
        // Ensure the URL has the full base URL
        const fullUrl = result.fileUrl.startsWith('http') 
          ? result.fileUrl 
          : `${API_BASE_URL}${result.fileUrl}`;
        
        console.log(`[VOManager] Setting processedAudioUrl to: ${fullUrl}`);

        // Clear old cached URLs to ensure new audio is used
        console.log('[VOManager] Clearing cached audio URLs after new upload');
        setCachedProcessedAudioUrl(null);
        setCachedLocalUrls({});

        // Save the monolithic processed-audio URL as the week-level processedVoUrl
        // (not localStorage, to avoid stale data).
        if (activeSpreadsheetId) {
          try {
            await chartDataService.setSetting(activeSpreadsheetId, 'processedVoUrl', fullUrl);
            console.log('[VOManager] Saved processed audio URL (processedVoUrl, authoritative source)');
          } catch (saveError) {
            console.error('[VOManager] Failed to save processed audio URL:', saveError);
            // Don't fail the entire operation if the save fails
          }
        }

        // If it's an S3 URL, cache it immediately for CORS-free playback
        if (fullUrl.includes('s3.amazonaws.com') || fullUrl.includes('s3.us-west-2.amazonaws.com')) {
          console.log('[VOManager] New upload is S3 URL, caching immediately for playback...');
          try {
            const cacheResponse = await fetch(`${SERVER_BASE_URL}/api/cache-s3-audio`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ s3Urls: [{ position: 'processedAudio', url: fullUrl }] }),
            });

            if (cacheResponse.ok) {
              const cacheResult = await cacheResponse.json();
              if (cacheResult.success && cacheResult.localUrls?.processedAudio) {
                const cachedUrl = `${SERVER_BASE_URL}${cacheResult.localUrls.processedAudio}`;
                console.log('[VOManager] Successfully cached new upload:', cachedUrl);
                setProcessedAudioUrl(cachedUrl);
                setCachedProcessedAudioUrl(cachedUrl);
                if (activeSpreadsheetId) {
                  localStorage.setItem(`processedAudioUrl_${activeSpreadsheetId}`, cachedUrl);
                }
              } else {
                console.error('[VOManager] Cache response missing localUrls, falling back to S3 URL');
                setProcessedAudioUrl(fullUrl);
              }
            } else {
              console.error('[VOManager] Cache request failed with status:', cacheResponse.status);
              setProcessedAudioUrl(fullUrl);
            }
          } catch (cacheError) {
            console.error('[VOManager] Error caching new upload:', cacheError);
            // Still set the S3 URL, user will see error when trying to play
            setProcessedAudioUrl(fullUrl);
          }
        } else {
          // Not an S3 URL, use directly
          setProcessedAudioUrl(fullUrl);
        }
      }

      // Update the transcription result with the new data
      setTranscriptionResult(result);

      // Save transcription result to localStorage to preserve original timestamps
      if (activeSpreadsheetId && result.timestamps) {
        localStorage.setItem(`transcriptionResult_${activeSpreadsheetId}`, JSON.stringify(result));
        console.log('[VOManager] Saved transcription result to localStorage for timestamp restoration');
      }

      // Handle auto-detected tease content (log only, don't write to sheet)
      if (result.teaseDetection && result.teaseDetection.hasTease && result.teaseDetection.teaseContent) {
        console.log('[VOManager] Auto-detected tease content:', result.teaseDetection.teaseContent);
        console.log('[VOManager] Tease detection confidence:', result.teaseDetection.confidence);
        console.log('[VOManager] Note: Not writing transcription to VO Text column (L15) - keeping manual guide text');

        // Show detection result but don't auto-populate the sheet
        enqueueSnackbar(`Detected tease content (not auto-populated): "${result.teaseDetection.teaseContent}"`, { variant: 'info' });
      } else if (result.teaseDetection && !result.teaseDetection.hasTease) {
        console.log('[VOManager] No tease detected - audio starts with Billboard intro');
      }
      
      // Update the in/out points based on the timestamps
      if (result.timestamps) {
        const newInPoints: { [key: string]: string } = {};
        const newOutPoints: { [key: string]: string } = {};
        
        Object.entries(result.timestamps).forEach(([key, value]) => {
          if (value.inPoint !== undefined) {
            newInPoints[key] = value.inPoint.toString();
          }
          if (value.outPoint !== undefined) {
            newOutPoints[key] = value.outPoint.toString();
          }
        });
        
        setVoInPoints(newInPoints);
        setVoOutPoints(newOutPoints);
        
        // Save the monolithic in/out points to the main inPoint/outPoint (N/O).
        if (activeSpreadsheetId) {
          try {
            console.log('[VOManager] Saving timestamps');
            const writes: Promise<void>[] = [];

            // Only keys that map to a real VO section are persisted.
            const validKeys: { [key: string]: number } = {
              'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
              '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
            };

            Object.entries(newInPoints).forEach(([key, inPoint]) => {
              if (validKeys[key]) {
                writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'inPoint', inPoint, activeLanguage));
                const outPoint = newOutPoints[key];
                if (outPoint) {
                  writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'outPoint', outPoint, activeLanguage));
                }
              }
            });

            if (writes.length > 0) {
              await Promise.all(writes);
              console.log(`[VOManager] Successfully saved ${writes.length} timestamp values`);
            }
          } catch (saveError) {
            console.error('Error saving timestamps:', saveError);
            // Continue even if saving fails - we'll still have the values in state
          }
        }
      }
      
      // Warn about script lines the alignment couldn't find (e.g. letter-spelled
      // titles like "EOO" that speech recognition mishears) — otherwise they
      // silently stay blank and the first sign of trouble is a broken export.
      // Prefer the server-computed list; fall back to comparing locally so older
      // servers still get the warning.
      const alignedKeys = result.timestamps ? Object.keys(result.timestamps) : [];
      const unalignedKeys = (result.missingLines && result.missingLines.length > 0)
        ? result.missingLines
        : Object.keys(voData).filter(k => voData[k] && voData[k].trim() !== '' && !alignedKeys.includes(k));
      if (unalignedKeys.length > 0) {
        const labels = unalignedKeys.map(k => ['Tease', 'Intro', 'Outro'].includes(k) ? k : `#${k}`).join(', ');
        enqueueSnackbar(
          `VO processed, but no timing was found for ${labels}. Set the in/out manually or upload audio for ${unalignedKeys.length === 1 ? 'that line' : 'those lines'} individually.`,
          { variant: 'warning', autoHideDuration: 15000 }
        );
      }

      setIsUploadSuccess(true);
      // Note: URL is already saved to localStorage earlier in this function
    } catch (err) {
      console.error('Error uploading file:', err);
      setError(`Error uploading file: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setIsProcessing(false);
      // Reset file input so the same file can be selected again
      inputElement.value = '';
    }
  };

  const handleClearVO = async () => {
    if (!activeSpreadsheetId) {
      setError('Cannot clear: No spreadsheet selected.');
      return;
    }

    try {
      // Clear the local state
      const clearedVoData: { [key: string]: string } = {};
      Object.keys(voData).forEach(key => {
        clearedVoData[key] = '';
      });
      setVoData(clearedVoData);

      // Clear in/out points
      setVoInPoints({});
      setVoOutPoints({});

      // Clear processed audio
      setProcessedAudioUrl(null);
      setCachedProcessedAudioUrl(null);
      setGeneratedAudio({});
      setGeneratedInPoints({});
      setGeneratedOutPoints({});
      setUploadedAudioFiles({});
      setLineAudioFiles({});
      setCachedLocalUrls({});

      // Clear localStorage data
      localStorage.removeItem(`processedAudioData_${activeSpreadsheetId}`);
      localStorage.removeItem(`voInPoints_${activeSpreadsheetId}`);
      localStorage.removeItem(`voOutPoints_${activeSpreadsheetId}`);
      localStorage.removeItem(`lineAudioFiles_${activeSpreadsheetId}`);

      // Clear VO data for positions 10..1 + Intro + Outro (NOT Tease — row 15 was
      // historically excluded from this bulk clear). For each section, clear the
      // script text (L), main in/out (N/O), line VO URL (S), and uploaded VO URL (V).
      // Line in/out (T/U) and line gain (P) are intentionally left untouched, matching
      // the original column-based clear.
      const keysToClear = ['10', '9', '8', '7', '6', '5', '4', '3', '2', '1', 'Intro', 'Outro'];
      const fieldsToClear = ['scriptText', 'inPoint', 'outPoint', 'lineVoUrl', 'uploadedVoUrl'];
      const writes: Promise<void>[] = [];
      for (const key of keysToClear) {
        for (const field of fieldsToClear) {
          writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), field, '', activeLanguage));
        }
      }
      // Clear the week-level processed-audio URL (was cell M14).
      writes.push(chartDataService.setSetting(activeSpreadsheetId, 'processedVoUrl', ''));

      await Promise.all(writes);
      console.log(`[VOManager] Successfully cleared ${writes.length} VO fields`);
    } catch (err) {
      console.error('Error clearing VO scripts:', err);
      setError(err instanceof Error ? err.message : 'Failed to clear VO scripts');
    }
  };

  const handleInPointClick = (key: string) => {
    if (isGeneratingAll) return;
    setEditingCell({ key: `in_${key}`, value: voInPoints[key] || '' });
  };

  const handleOutPointClick = (key: string) => {
    if (isGeneratingAll) return;
    setEditingCell({ key: `out_${key}`, value: voOutPoints[key] || '' });
  };

  const handleInPointSave = (key: string, value: string) => {
    const updated = { ...voInPoints, [key]: value };
    setVoInPoints(updated);
    setEditingCell(null);

    // Persist to localStorage
    if (activeSpreadsheetId) {
      localStorage.setItem(`voInPoints_${activeSpreadsheetId}`, JSON.stringify(updated));
    }

    // Persist to the line in-point (T). validKeys gates editable sections.
    const validKeys: { [key: string]: number } = {
      'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
      '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
    };
    if (validKeys[key] && activeSpreadsheetId) {
      chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineInPoint', value, activeLanguage)
        .catch(err => console.error(`[VOManager] Failed to save in point for ${key}:`, err));
    }
  };

  const handleOutPointSave = (key: string, value: string) => {
    const updated = { ...voOutPoints, [key]: value };
    setVoOutPoints(updated);
    setEditingCell(null);

    // Persist to localStorage
    if (activeSpreadsheetId) {
      localStorage.setItem(`voOutPoints_${activeSpreadsheetId}`, JSON.stringify(updated));
    }

    // Persist to the line out-point (U). validKeys gates editable sections.
    const validKeys: { [key: string]: number } = {
      'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
      '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
    };
    if (validKeys[key] && activeSpreadsheetId) {
      chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineOutPoint', value, activeLanguage)
        .catch(err => console.error(`[VOManager] Failed to save out point for ${key}:`, err));
    }
  };

  const handleGainClick = (key: string) => {
    if (isGeneratingAll) return;
    const currentGain = lineGainSettings[key] || 0;
    setEditingCell({ key: `gain_${key}`, value: currentGain === 0 ? '' : currentGain.toString() });
  };

  const handleGainSave = async (key: string, value: string) => {
    const trimmedValue = value.trim();

    // Handle empty string as removal of line-specific gain override (will use global gain)
    if (trimmedValue === '') {
      setLineGainSettings(prev => {
        const updatedSettings = { ...prev };
        delete updatedSettings[key]; // Remove the line-specific override, will use global gain
        if (activeSpreadsheetId) {
          try {
            localStorage.setItem(`lineGainSettings_${activeSpreadsheetId}`, JSON.stringify(updatedSettings));
            console.log(`[VOManager] Removed line gain override for ${key}, will use global gain. Saved to localStorage:`, updatedSettings);
          } catch (e) {
            console.error('[VOManager] Error saving lineGainSettings to localStorage:', e);
          }
        }
        return updatedSettings;
      });

      // Also remove from audioExportSettings for OutputManager
      if (activeSpreadsheetId) {
        try {
          const exportKey = `audioExportSettings_${activeSpreadsheetId}`;
          const existingSettings = JSON.parse(localStorage.getItem(exportKey) || '{}');
          delete existingSettings[`lineGain_${key}`];
          localStorage.setItem(exportKey, JSON.stringify(existingSettings));
          console.log(`[VOManager] Removed lineGain_${key} from audioExportSettings`);
        } catch (e) {
          console.error('[VOManager] Error removing line gain from audioExportSettings:', e);
        }
      }

      // Clear the line gain (P). LINE_GAIN_MAP gates editable sections.
      if (LINE_GAIN_MAP[key] && activeSpreadsheetId) {
        try {
          await chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineGain', '', activeLanguage);
          console.log(`[VOManager] Cleared line gain for ${key}`);
        } catch (saveError) {
          console.error(`[VOManager] Failed to clear line gain for ${key}:`, saveError);
        }
      }
    } else {
      const numericValue = parseFloat(trimmedValue);
      if (!isNaN(numericValue)) {
        const clampedValue = Math.max(-24, Math.min(24, numericValue));
        setLineGainSettings(prev => {
          const updatedSettings = { ...prev, [key]: clampedValue };
          if (activeSpreadsheetId) {
            try {
              localStorage.setItem(`lineGainSettings_${activeSpreadsheetId}`, JSON.stringify(updatedSettings));
              console.log(`[VOManager] Saved line gain for ${key}: ${clampedValue}dB. Full settings:`, updatedSettings);
            } catch (e) {
              console.error('[VOManager] Error saving lineGainSettings to localStorage:', e);
            }
          }
          return updatedSettings;
        });

        // Also save to audioExportSettings for OutputManager
        if (activeSpreadsheetId) {
          try {
            const exportKey = `audioExportSettings_${activeSpreadsheetId}`;
            const existingSettings = JSON.parse(localStorage.getItem(exportKey) || '{}');
            existingSettings[`lineGain_${key}`] = String(clampedValue);
            localStorage.setItem(exportKey, JSON.stringify(existingSettings));
            console.log(`[VOManager] Saved lineGain_${key}=${clampedValue} to audioExportSettings`);
          } catch (e) {
            console.error('[VOManager] Error saving line gain to audioExportSettings:', e);
          }
        }

        // Save the line gain (P). LINE_GAIN_MAP gates editable sections.
        if (LINE_GAIN_MAP[key] && activeSpreadsheetId) {
          try {
            await chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineGain', clampedValue.toString(), activeLanguage);
            console.log(`[VOManager] Saved line gain for ${key}: ${clampedValue}dB`);
          } catch (saveError) {
            console.error(`[VOManager] Failed to save line gain for ${key}:`, saveError);
          }
        }
      } else {
        console.warn(`[VOManager] Invalid gain value "${trimmedValue}" for key ${key}, not saving.`);
      }
    }
    setEditingCell(null);
  };

  const handleSaveSettings = (newSettings: typeof voiceSettings) => {
    setVoiceSettings(newSettings);
    // Save all settings to localStorage
    localStorage.setItem('voiceSettings_voiceId', newSettings.voiceId);
    localStorage.setItem('voiceSettings_stability', String(newSettings.stability));
    localStorage.setItem('voiceSettings_similarityBoost', String(newSettings.similarityBoost));
    localStorage.setItem('voiceSettings_style', String(newSettings.style));
    localStorage.setItem('voiceSettings_speed', String(newSettings.speed));
    setIsSettingsOpen(false);
  };

  const generateAudio = async (key: string, text: string) => {
    if (!text || !activeSpreadsheetId) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/generate-audio`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text,
          voiceId: voiceSettings.voiceId,
          stability: voiceSettings.stability,
          similarityBoost: voiceSettings.similarityBoost,
          style: voiceSettings.style,
          speed: voiceSettings.speed,
          // Add folder organization parameters
          voSubfolder: currentChartConfig?.voSubfolder || '',
          sheetDate: sheetDate || '',
          key: key, // e.g., "Tease", "1", "2", etc.
          language: activeLanguage, // bilingual: generated audio → /es/ path
        }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      const s3Url = result.audioUrl; // Clean S3 URL for storage
      // Add cache-busting timestamp to prevent browser from using cached audio after regeneration
      const cacheBustedUrl = `${s3Url}?t=${Date.now()}`;
      setGeneratedAudio(prev => ({ ...prev, [key]: cacheBustedUrl }));

      // Also set lineAudioFiles so export (OutputManager) can find the VO
      setLineAudioFiles(prev => ({ ...prev, [key]: s3Url }));

      // Save to localStorage
      const lineAudioStorage = JSON.parse(localStorage.getItem(`lineAudioFiles_${activeSpreadsheetId}`) || '{}');
      lineAudioStorage[key] = s3Url;
      localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, JSON.stringify(lineAudioStorage));

      // Cache the S3 URL locally for waveform editor (avoids CORS issues)
      if (s3Url.includes('s3.amazonaws.com') || s3Url.includes('s3.us-west-2.amazonaws.com')) {
        try {
          console.log(`[VOManager] Caching generated S3 audio for ${key}...`);
          // Server expects s3Urls as an array of { position, url } (see /api/cache-s3-audio).
          const cacheResponse = await fetch(`${SERVER_BASE_URL}/api/cache-s3-audio`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ s3Urls: [{ position: key, url: s3Url }] })
          });
          if (cacheResponse.ok) {
            const cacheResult = await cacheResponse.json();
            if (cacheResult.localUrls && cacheResult.localUrls[key]) {
              setCachedLocalUrls(prev => ({ ...prev, [key]: cacheResult.localUrls[key] }));
              console.log(`[VOManager] Cached generated S3 audio for ${key}: ${cacheResult.localUrls[key]}`);
            }
          }
        } catch (cacheError) {
          console.warn(`[VOManager] Failed to cache generated S3 audio for ${key}:`, cacheError);
        }
      }

      // Get audio duration to set in/out points (generated audio has no silence padding)
      const duration = await new Promise<number>((resolve) => {
        const audio = new Audio(cacheBustedUrl);
        audio.addEventListener('loadedmetadata', () => {
          resolve(parseFloat(audio.duration.toFixed(2)));
        });
        audio.addEventListener('error', () => {
          console.warn(`[VOManager] Could not get duration for generated audio ${key}, defaulting to 3s`);
          resolve(3);
        });
      });

      // Trim first and last 0.1s to remove ElevenLabs pop artifacts
      const trimAmount = 0.1;
      const inPoint = parseFloat(Math.min(trimAmount, duration / 2).toFixed(2));
      const outPoint = parseFloat(Math.max(duration - trimAmount, duration / 2).toFixed(2));
      console.log(`[VOManager] Generated audio for ${key}: duration=${duration}s, setting inPoint=${inPoint}, outPoint=${outPoint} (trimmed ${trimAmount}s from each end)`);

      // Update in/out point state
      setVoInPoints(prev => ({ ...prev, [key]: inPoint.toString() }));
      setVoOutPoints(prev => ({ ...prev, [key]: outPoint.toString() }));
      // Store generated in/out points separately so they survive mode switches
      setGeneratedInPoints(prev => ({ ...prev, [key]: inPoint.toString() }));
      setGeneratedOutPoints(prev => ({ ...prev, [key]: outPoint.toString() }));

      // Save the line VO URL (S) + line in/out points (T/U).
      const writes: Promise<void>[] = [];
      if (LINE_VO_MAP[key] && activeSpreadsheetId) {
        writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineVoUrl', s3Url, activeLanguage));
      }
      // validKeys gates editable sections.
      const validKeys: { [key: string]: number } = {
        'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
        '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
      };
      if (validKeys[key] && activeSpreadsheetId) {
        writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineInPoint', inPoint.toString(), activeLanguage));
        writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineOutPoint', outPoint.toString(), activeLanguage));
      }

      if (writes.length > 0) {
        try {
          await Promise.all(writes);
          console.log(`[VOManager] Saved generated VO URL and in/out points for ${key}`);
        } catch (saveError) {
          console.error(`[VOManager] Failed to save generated VO data:`, saveError);
        }
      }

      return cacheBustedUrl;
    } catch (err) {
      console.error(`Error generating audio for ${key}:`, err);
      setError(`Error generating audio for ${key}: ${err instanceof Error ? err.message : String(err)}`);
      throw err;
    }
  };

  // Handle single line audio generation
  const handleGenerateLine = async (key: string) => {
    const text = voData[key];
    if (!text || !text.trim()) {
      setError(`No script text for ${key}. Please enter text first.`);
      return;
    }

    setIsGeneratingLine(key);
    setError(null);

    try {
      const audioUrl = await generateAudio(key, text);
      if (audioUrl) {
        enqueueSnackbar(`Generated audio for ${key}`, { variant: 'success', autoHideDuration: 2000 });
      }
    } catch (err) {
      // Error already handled in generateAudio
    } finally {
      setIsGeneratingLine(null);
    }
  };

  const handleScriptSave = async (parsedScript: any) => {
    setScriptDialogOpen(false);
    
    console.log('[handleScriptSave] Starting script save with data:', parsedScript);
    
    // Update the VO data with the new script
    const newVoData = { ...voData };
    
    // Handle tease if present (new field for tease detection)
    if (typeof parsedScript.tease === 'string') {
      if (parsedScript.tease) {
        newVoData['Tease'] = parsedScript.tease;
        console.log(`[handleScriptSave] Setting Tease: "${parsedScript.tease}"`);
      } else {
        // Empty string means no tease detected - clear any existing tease
        newVoData['Tease'] = '';
        console.log('[handleScriptSave] No tease detected - clearing Tease field');
      }
    }
    
    // Handle intro if present
    if (typeof parsedScript.intro === 'string') {
      newVoData['Intro'] = parsedScript.intro;
      console.log(`[handleScriptSave] Setting Intro: "${parsedScript.intro}"`);
    }
    
    // Handle the songs array
    if (Array.isArray(parsedScript.songs)) {
      console.log(`[handleScriptSave] Processing ${parsedScript.songs.length} songs`);
      
      // Map songs array to positions (10 down to 1)
      parsedScript.songs.forEach((songText: string, index: number) => {
        // Calculate position in reverse order - index 0 should be 10, index 1 should be 9, etc.
        const position = (10 - index).toString();
        console.log(`[handleScriptSave] Mapping song ${index} to position ${position}: "${songText}"`);
        newVoData[position] = songText;
      });
    } else {
      console.log('[handleScriptSave] No songs array found in parsedScript');
    }
    
    // Handle outro if present
    if (typeof parsedScript.outro === 'string') {
      newVoData['Outro'] = parsedScript.outro;
      console.log(`[handleScriptSave] Setting Outro: "${parsedScript.outro}"`);
      }
    
    // Log the final newVoData object
    console.log('[handleScriptSave] Final newVoData:', Object.keys(newVoData));
    
    setVoData(newVoData);
    
    // Save the script text (column L = scriptText) for each mappable section.
    if (activeSpreadsheetId) {
      try {
        // Only entries whose key maps to a VO section (CELL_MAP) are persisted.
        const entries = Object.entries(newVoData).filter(([key]) => CELL_MAP[key] !== undefined);
        console.log(`[handleScriptSave] Total updates to apply: ${entries.length}`);

        if (entries.length > 0) {
          console.log(`[handleScriptSave] Applying ${entries.length} scriptText updates...`);
          try {
            for (const [key, value] of entries) {
              console.log(`[handleScriptSave] Sending update: ${keyToSection(key)} scriptText = ${JSON.stringify(value)}`);
              await chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'scriptText', value, activeLanguage);
              console.log(`[handleScriptSave] Update sent successfully: ${keyToSection(key)}`);
            }
            console.log('[handleScriptSave] Updates applied successfully.');
          } catch (updateErr) {
            console.error('[handleScriptSave] Error during update:', updateErr);
            throw updateErr; // Re-throw to be caught by outer catch
          }
        } else {
          console.log('[handleScriptSave] No valid updates to apply after filtering.');
        }

      } catch (err) {
        console.error('Error saving script:', err);
        setError(`Error saving script: ${err instanceof Error ? err.message : String(err)}`);
        enqueueSnackbar(`Failed to save script: ${err instanceof Error ? err.message : String(err)}`, { variant: 'error' });
      }
    } else {
      console.error('[handleScriptSave] No active spreadsheet ID found');
      setError('No active spreadsheet ID found. Please select a chart date first.');
      enqueueSnackbar('No active spreadsheet ID found. Please select a chart date first.', { variant: 'error' });
    }
  };

  const handleLimiterChange = async (newSettings: {
    gain: number; // This is the makeupGain we want to save
  }) => {
    console.log(`[VOManager handleLimiterChange] Called with gain: ${newSettings.gain}, activeSpreadsheetId: ${activeSpreadsheetId}`);
    setLimiterSettings(newSettings);
    localStorage.setItem('limiterSettings_gain', String(newSettings.gain));
    console.log(`[VOManager handleLimiterChange] Saved limiterSettings_gain to localStorage: ${newSettings.gain}`);

    // --- Save settings for OutputManager export --- START ---
    if (!activeSpreadsheetId) {
      console.error('[VOManager] Cannot save settings to localStorage for OutputManager: activeSpreadsheetId is missing.');
      return;
    }
    const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
    const existing = JSON.parse(localStorage.getItem(storageKey) || '{}');
    const settingsForExport = {
      ...existing, // Preserve lineGain_* entries
      makeupGain: String(newSettings.gain), // Update global gain
    };
    try {
      localStorage.setItem(storageKey, JSON.stringify(settingsForExport));
      console.log(`[VOManager handleLimiterChange] Saved audio export settings to localStorage (${storageKey}), preserved line gains:`, settingsForExport);

      // Verify it was saved correctly
      const verification = localStorage.getItem(storageKey);
      console.log(`[VOManager handleLimiterChange] Verification - localStorage now contains:`, verification);
    } catch (error) {
      console.error('[VOManager] Failed to save audio export settings to localStorage:', error);
    }
    // --- Save settings for OutputManager export --- END ---

    // Save the global gain (week-level globalGain). Per-line gain overrides are a
    // separate field and are not touched when the global gain changes.
    try {
      await chartDataService.setSetting(activeSpreadsheetId, 'globalGain', newSettings.gain.toString());
      console.log(`[VOManager] Saved global gain: ${newSettings.gain}dB (line-specific gains preserved)`);
    } catch (saveError) {
      console.error('[VOManager] Failed to save global gain:', saveError);
    }
  };

  // Add a function to restore global gain settings (call this when you want to reset export to global gain)
  const restoreGlobalGainToExport = useCallback(() => {
    if (!activeSpreadsheetId) {
      console.warn('[VOManager] Cannot restore global gain: activeSpreadsheetId is missing.');
      return;
    }

    const currentGlobalGain = limiterSettings.gain;
    const storageKey = `audioExportSettings_${activeSpreadsheetId}`;
    const existing = JSON.parse(localStorage.getItem(storageKey) || '{}');
    const settingsForExport = {
      ...existing, // Preserve lineGain_* entries
      makeupGain: String(currentGlobalGain),
    };

    try {
      localStorage.setItem(storageKey, JSON.stringify(settingsForExport));
      console.log(`[VOManager restoreGlobalGainToExport] Restored global gain ${currentGlobalGain}dB to export settings, preserved line gains (${storageKey})`);
    } catch (error) {
      console.error('[VOManager] Failed to restore global gain to export settings:', error);
    }
  }, [activeSpreadsheetId, limiterSettings.gain]);

  const handleOpenWaveformEditor = async (key: string) => {
    let audioUrl: string | null = null;
    let inPoint: number | null = null;
    let outPoint: number | null = null;
    let isDefaultOutro = false;
    let isGeneratedAudio = false;

    // Handle generate mode - use generated audio URL
    if (isGenerateMode && generatedAudio[key]) {
      let generatedUrl = generatedAudio[key];
      isGeneratedAudio = true;

      // Use existing in/out points if they exist, otherwise default to 0 and full duration
      if (voInPoints[key] && voOutPoints[key]) {
        inPoint = parseFloat(voInPoints[key]);
        outPoint = parseFloat(voOutPoints[key]);
        console.log(`[VOManager handleOpenWaveformEditor] Using existing in/out points for GENERATED audio: ${key}, in=${inPoint}, out=${outPoint}`);
      } else {
        inPoint = 0;
        outPoint = null; // Will be set to full duration by waveform editor
        console.log(`[VOManager handleOpenWaveformEditor] No existing in/out points for GENERATED audio: ${key}, using defaults`);
      }
      console.log(`[VOManager handleOpenWaveformEditor] Preparing editor for GENERATED audio: ${key}`);

      // Check if we need to cache the S3 URL
      // Strip any query params (cache busting) for the S3 check
      const baseUrl = generatedUrl.split('?')[0];
      if (baseUrl.includes('s3.amazonaws.com')) {
        console.log(`[VOManager handleOpenWaveformEditor] Caching S3 URL for waveform editor...`);
        try {
          const cacheResponse = await fetch(`${SERVER_BASE_URL}/api/cache-s3-audio`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ s3Urls: [{ position: key, url: baseUrl }] }),
          });

          if (cacheResponse.ok) {
            const cacheResult = await cacheResponse.json();
            if (cacheResult.success && cacheResult.localUrls?.[key]) {
              audioUrl = `${SERVER_BASE_URL}${cacheResult.localUrls[key]}`;
              console.log(`[VOManager handleOpenWaveformEditor] Cached generated audio: ${audioUrl}`);
            } else {
              console.error('[VOManager handleOpenWaveformEditor] Cache response missing localUrls');
              alert('Failed to cache audio for waveform editor. Please try again.');
              return;
            }
          } else {
            console.error('[VOManager handleOpenWaveformEditor] Cache request failed');
            alert('Failed to cache audio for waveform editor. Please try again.');
            return;
          }
        } catch (cacheError) {
          console.error('[VOManager handleOpenWaveformEditor] Error caching audio:', cacheError);
          alert('Failed to cache audio for waveform editor. Please try again.');
          return;
        }
      } else {
        // Not an S3 URL (maybe already cached or local), use as-is
        audioUrl = generatedUrl;
      }
    }
    // Determine the source and points for upload mode
    else if (key === 'Outro' && (
      // Use default outro when script is empty OR both in/out points are 0
      (!voData[key] || voData[key].trim() === '') ||
      (voInPoints[key] === '0' && voOutPoints[key] === '0')
    )) {
      // Special case: Default Outro with empty script or zero in/out points
      audioUrl = getDefaultOutroAudioUrl(currentChartConfig?.voSubfolder || 'hot-100');
      inPoint = 0;
      outPoint = getDefaultOutroDuration(currentChartConfig?.voSubfolder); // per-chart default outro length
      isDefaultOutro = true;
      console.log('[VOManager handleOpenWaveformEditor] Preparing editor for DEFAULT Outro.');
    } else {
      // Standard case (or Outro with script)
      // Use cached local URL for browser playback, fallback to S3 URLs if no cache
      audioUrl = cachedLocalUrls[key] || lineAudioFiles[key] || cachedProcessedAudioUrl || processedAudioUrl;

      // If the audio URL is an S3 URL and we don't have a cached version yet, wait for caching
      if (audioUrl && typeof audioUrl === 'string' && (audioUrl.includes('s3.amazonaws.com') || audioUrl.includes('s3.us-west-2.amazonaws.com'))) {
        // Check if we have a cached version for this specific case
        const cachedUrl = cachedLocalUrls[key] ||
                         (lineAudioFiles[key] && typeof lineAudioFiles[key] === 'string' && (lineAudioFiles[key].includes('s3.amazonaws.com') || lineAudioFiles[key].includes('s3.us-west-2.amazonaws.com')) ? null : lineAudioFiles[key]) ||
                         cachedProcessedAudioUrl;

        if (!cachedUrl) {
          console.warn(`[VOManager handleOpenWaveformEditor] Cannot open editor for key '${key}': S3 URL needs to be cached first. Please wait a moment and try again.`);
          alert('Audio is being loaded from cloud storage. Please wait a moment and try again.');
          return;
        }
        audioUrl = cachedUrl;
      }

      if (voInPoints[key] && voOutPoints[key]) {
        inPoint = parseFloat(voInPoints[key]);
        outPoint = parseFloat(voOutPoints[key]);
      } else {
        // If standard case is missing points, we cannot open
        console.warn(`[VOManager handleOpenWaveformEditor] Cannot open editor for key '${key}': Missing in/out points.`);
        return;
      }
    }

    // Check if we have a valid URL and points before proceeding
    // For generated audio, outPoint can be null (will use full duration)
    if (!audioUrl || inPoint === null || (!isGeneratedAudio && (outPoint === null || isNaN(outPoint))) || isNaN(inPoint)) {
      console.warn(`[VOManager handleOpenWaveformEditor] Cannot open editor for key '${key}': Invalid URL or points (URL: ${audioUrl}, In: ${inPoint}, Out: ${outPoint}).`);
      return;
    }

    // Use line-specific gain if set, otherwise use global gain (same logic as playLine)
    const hasLineSpecificGain = lineGainSettings[key] !== undefined;
    let gainToPass: number;
    if (key === 'Outro' && (!voData[key] || voData[key].trim() === '') && !lineAudioFiles[key] && !hasLineSpecificGain) {
      gainToPass = 0; // Default Outro uses 0dB
    } else if (isGeneratedAudio) {
      gainToPass = hasLineSpecificGain ? lineGainSettings[key] : 0; // Generated audio starts at 0dB
    } else {
      gainToPass = hasLineSpecificGain ? lineGainSettings[key] : limiterSettings.gain;
    }
    console.log(`[VOManager handleOpenWaveformEditor] Preparing editor data for key '${key}' (DefaultOutro: ${isDefaultOutro}, Generated: ${isGeneratedAudio}) with initialGain: ${gainToPass}dB (${hasLineSpecificGain ? 'line-specific' : 'global'})`);

    // Set the editor data FIRST
    // For generated audio without existing points, use a large outPoint that will be clamped to duration by the editor
    setEditorData({
      lineKey: key,
      initialInPoint: inPoint,
      initialOutPoint: (isGeneratedAudio && outPoint === null) ? 9999 : outPoint!, // Only use 9999 if no existing points
      initialGain: gainToPass,
      audioUrl: audioUrl,
      songTitle: isGeneratedAudio ? `Generated: ${key}` : (isDefaultOutro ? 'Default Outro' : songTitles[key]),
      songArtist: isGeneratedAudio ? '' : (isDefaultOutro ? '' : songArtists[key])
    });
    
    // Set the key we want to open
    setPendingEditorKey(key);
  };

  // Effect to open the editor *after* editorData has been set
  useEffect(() => {
    if (pendingEditorKey && editorData?.lineKey === pendingEditorKey) {
      console.log(`[VOManager Effect] editorData updated for key '${pendingEditorKey}', opening dialog.`);
    setIsEditorOpen(true);
      setPendingEditorKey(null); // Reset pending state
    }
  }, [editorData, pendingEditorKey]); // Depend on editorData and the pending key

  // Add onClose handler to clear editorData when dialog closes
  const handleCloseEditor = () => {
    setIsEditorOpen(false);
    setEditorData(null); // Clear data to ensure fresh state next time
  }

  const handleSaveEditedTimestamps = (key: string, inPoint: number, outPoint: number) => {
    console.log(`[VOManager] Saving edited timestamps for key ${key}: In=${inPoint}, Out=${outPoint} (gain not modified)`);

    // Update voInPoints and voOutPoints only - don't touch gain settings
    const updatedInPoints = { ...voInPoints, [key]: inPoint.toString() };
    const updatedOutPoints = { ...voOutPoints, [key]: outPoint.toString() };
    setVoInPoints(updatedInPoints);
    setVoOutPoints(updatedOutPoints);

    // Persist the edited timestamps.
    if (activeSpreadsheetId) {
      // Sync localStorage so the next mount's hydration doesn't overwrite the edit
      // with stale values before fetchVOData() returns.
      localStorage.setItem(`voInPoints_${activeSpreadsheetId}`, JSON.stringify(updatedInPoints));
      localStorage.setItem(`voOutPoints_${activeSpreadsheetId}`, JSON.stringify(updatedOutPoints));

      // Always write to the per-line override fields (lineInPoint/lineOutPoint, T/U).
      // The reader prefers these whenever they're non-empty, and the text-input save
      // handlers also write here, so all user edits go to the same place. The main
      // inPoint/outPoint (N/O) remains the original alignment output and is never
      // overwritten by user edits. validKeys gates editable sections.
      const validKeys: { [key: string]: number } = {
        'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
        '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
      };
      if (validKeys[key]) {
        console.log('[VOManager][handleSaveEditedTimestamps] About to write line in/out for', key);
        Promise.all([
          chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineInPoint', inPoint.toFixed(2), activeLanguage),
          chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineOutPoint', outPoint.toFixed(2), activeLanguage),
        ])
          .then(() => {
            enqueueSnackbar(`Timestamps for ${key} saved.`, { variant: 'success' });
          })
          .catch(err => {
            console.error(`[VOManager] Error saving timestamps for ${key}:`, err);
            enqueueSnackbar(`Failed to save timestamps for ${key}: ${err.message}`, { variant: 'error' });
          });
      }
    }
    setEditorData(null); // Use the correct state setter to close the dialog
  };

  const handlePeaksGenerated = (peaks: number[]) => {
    setProcessedAudioPeaks(peaks);
    // Not saving to localStorage anymore to avoid stale data issues
  };

  // Load VO data when spreadsheet changes
  useEffect(() => {
    if (activeSpreadsheetId) {
      fetchVOData();
    }
  }, [activeSpreadsheetId, fetchVOData]);

  // Add a function to handle line audio deletion
  const handleLineFileDelete = async (key: string) => {
    if (!activeSpreadsheetId) return;

    // Confirm deletion
    if (!window.confirm(`Are you sure you want to delete the Line VO for ${key}?`)) {
      return;
    }

    try {
      // Remove line audio file from state
      setLineAudioFiles(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });

      // Remove line audio file from localStorage
      const lineAudioStorage = JSON.parse(localStorage.getItem(`lineAudioFiles_${activeSpreadsheetId}`) || '{}');
      delete lineAudioStorage[key];
      localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, JSON.stringify(lineAudioStorage));

      // Collect the VO field clears (lineVoUrl S, uploadedVoUrl V, lineInPoint T,
      // lineOutPoint U). The main inPoint/outPoint (N/O) remain intact.
      const writes: Promise<void>[] = [];

      // Remove the line VO URL (S)
      if (LINE_VO_MAP[key]) {
        writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineVoUrl', '', activeLanguage));
        console.log(`[VOManager] Will clear line VO URL for ${key}`);
      }

      // Also clear the uploaded VO URL (V)
      setUploadedAudioFiles(prev => {
        const newState = { ...prev };
        delete newState[key];
        return newState;
      });
      if (UPLOADED_VO_MAP[key]) {
        writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'uploadedVoUrl', '', activeLanguage));
        console.log(`[VOManager] Will clear uploaded VO URL for ${key}`);
      }

      // Clear the line in/out (T/U) - original timestamps in N/O remain intact.
      // validKeys gates editable sections.
      const validKeys: { [key: string]: number } = {
        'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
        '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
      };
      if (validKeys[key]) {
        writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineInPoint', '', activeLanguage));
        writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineOutPoint', '', activeLanguage));
        console.log(`[VOManager] Will clear line-specific timestamps for ${key} (original timestamps preserved)`);
      }

      // Clear line-specific gain settings
      setLineGainSettings(prev => {
        const newSettings = { ...prev };
        delete newSettings[key];
        return newSettings;
      });

      // Remove line gain from localStorage
      const lineGainStorage = JSON.parse(localStorage.getItem(`lineGainSettings_${activeSpreadsheetId}`) || '{}');
      delete lineGainStorage[key];
      localStorage.setItem(`lineGainSettings_${activeSpreadsheetId}`, JSON.stringify(lineGainStorage));

      // Remove line gain from audioExportSettings
      const exportKey = `audioExportSettings_${activeSpreadsheetId}`;
      const existingSettings = JSON.parse(localStorage.getItem(exportKey) || '{}');
      delete existingSettings[`lineGain_${key}`];
      localStorage.setItem(exportKey, JSON.stringify(existingSettings));

      console.log(`[VOManager] Cleared line-specific gain for ${key}`);

      // Clear cached local URL for this line
      setCachedLocalUrls(prev => {
        const newUrls = { ...prev };
        delete newUrls[key];
        return newUrls;
      });

      // If this line is currently playing, stop it
      if (playingKey === key) {
        stopAudio();
        console.log(`[VOManager] Stopped playback of deleted line ${key}`);
      }

      // Persist the VO field clears.
      if (writes.length > 0) {
        await Promise.all(writes);
        console.log(`[VOManager] Successfully updated ${writes.length} VO fields`);
      }

      // Reload data to update state with original timestamps from the main in/out.
      await fetchVOData();
      console.log(`[VOManager] Reloaded VO data to apply original timestamps for ${key}`);

      enqueueSnackbar(`Line VO for ${key} deleted - reverted to original`, { variant: 'success' });
    } catch (err) {
      console.error(`Error deleting line VO for ${key}:`, err);
      setError(`Error deleting line VO for ${key}: ${err instanceof Error ? err.message : String(err)}`);
      enqueueSnackbar(`Error deleting Line VO for ${key}`, { variant: 'error' });
    }
  };

  // Add a function to handle line audio upload
  const handleLineFileUpload = async (key: string, event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !activeSpreadsheetId) return;

    // Guard: the per-line upload endpoint requires script text for STT, so an
    // empty/default (placeholder) box would be rejected with a 400. The upload
    // button is already disabled in this state; this is a belt-and-suspenders
    // check in case the file input is triggered another way.
    const lineText = voData[key] || '';
    if (!lineText.trim()) {
      enqueueSnackbar(`Add VO script text for ${['Tease', 'Intro', 'Outro'].includes(key) ? key : `Song ${key}`} before uploading audio.`, { variant: 'warning' });
      event.target.value = ''; // allow re-selecting the same file after adding text
      return;
    }

    setIsUploadingLine(key);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('audio', file);
      formData.append('lineKey', key);
      formData.append('lineText', lineText); // Add line text for transcription

      // Add sheetDate for S3 folder structure
      if (sheetDate) {
        formData.append('sheetDate', sheetDate);
        console.log(`[VOManager] Uploading line audio for ${key}: ${file.name} with sheetDate: ${sheetDate}, text: "${lineText}"`);
      } else {
        console.log(`[VOManager] Uploading line audio for ${key}: ${file.name} (no sheetDate available), text: "${lineText}"`);
      }
      if (currentChartConfig?.voSubfolder) {
        formData.append('voSubfolder', currentChartConfig.voSubfolder);
      }
      formData.append('language', activeLanguage); // bilingual: ES audio → /es/ path

      const response = await fetch(`${API_BASE_URL}/api/process-line-audio`, {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      if (result.fileUrl) {
        // Ensure the URL has the full base URL
        const fullUrl = result.fileUrl.startsWith('http')
          ? result.fileUrl
          : `${API_BASE_URL}${result.fileUrl}`;

        console.log(`[VOManager] Setting line audio URL for ${key} to: ${fullUrl}`);
        setLineAudioFiles(prev => ({ ...prev, [key]: fullUrl }));

        // Save to localStorage
        const lineAudioStorage = JSON.parse(localStorage.getItem(`lineAudioFiles_${activeSpreadsheetId}`) || '{}');
        lineAudioStorage[key] = fullUrl;
        localStorage.setItem(`lineAudioFiles_${activeSpreadsheetId}`, JSON.stringify(lineAudioStorage));

        // Cache the S3 URL locally for waveform editor (avoids CORS issues)
        if (fullUrl.includes('s3.amazonaws.com') || fullUrl.includes('s3.us-west-2.amazonaws.com')) {
          try {
            console.log(`[VOManager] Caching S3 audio for ${key}...`);
            const cacheResponse = await fetch(`${SERVER_BASE_URL}/api/cache-s3-audio`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              // Server expects s3Urls as an array of { position, url } (see /api/cache-s3-audio).
              // Passing an object map here was rejected with 400.
              body: JSON.stringify({ s3Urls: [{ position: key, url: fullUrl }] })
            });
            if (cacheResponse.ok) {
              const cacheResult = await cacheResponse.json();
              if (cacheResult.localUrls && cacheResult.localUrls[key]) {
                setCachedLocalUrls(prev => ({ ...prev, [key]: cacheResult.localUrls[key] }));
                console.log(`[VOManager] Cached S3 audio for ${key}: ${cacheResult.localUrls[key]}`);
              }
            }
          } catch (cacheError) {
            console.warn(`[VOManager] Failed to cache S3 audio for ${key}:`, cacheError);
          }
        }

        // Collect the VO field writes (lineVoUrl S, uploadedVoUrl V, line in/out T/U).
        const writes: Promise<void>[] = [];

        // Save the line VO URL (S)
        if (LINE_VO_MAP[key]) {
          writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineVoUrl', fullUrl, activeLanguage));
          console.log(`[VOManager] Will save line VO URL for ${key}: ${fullUrl}`);
        }

        // Also save the uploaded VO URL (V) and uploadedAudioFiles state
        setUploadedAudioFiles(prev => ({ ...prev, [key]: fullUrl }));
        if (UPLOADED_VO_MAP[key]) {
          writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'uploadedVoUrl', fullUrl, activeLanguage));
          console.log(`[VOManager] Will save uploaded VO URL for ${key}: ${fullUrl}`);
        }

        // Save in/out points if transcription succeeded
        const hasTimestamps = result.inPoint !== null && result.inPoint !== undefined &&
            result.outPoint !== null && result.outPoint !== undefined;
        if (hasTimestamps) {

          console.log(`[VOManager] Transcription returned inPoint: ${result.inPoint}s, outPoint: ${result.outPoint}s`);

          // Update state
          setVoInPoints(prev => ({ ...prev, [key]: result.inPoint.toString() }));
          setVoOutPoints(prev => ({ ...prev, [key]: result.outPoint.toString() }));

          // validKeys gates editable sections.
          const validKeys: { [key: string]: number } = {
            'Tease': 15, 'Intro': 12, '10': 2, '9': 3, '8': 4, '7': 5, '6': 6,
            '5': 7, '4': 8, '3': 9, '2': 10, '1': 11, 'Outro': 13
          };

          if (validKeys[key]) {
            // Use line in/out (T/U) to preserve original timestamps in the main in/out (N/O)
            writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineInPoint', result.inPoint.toString(), activeLanguage));
            writes.push(chartDataService.setVoField(activeSpreadsheetId, keyToSection(key), 'lineOutPoint', result.outPoint.toString(), activeLanguage));
            console.log(`[VOManager] Will save line-specific in/out points for ${key}`);
          }
        } else {
          // The clip uploaded fine but the server couldn't detect where the line
          // is spoken — without in/out points the line can't play or export, so
          // tell the user instead of failing silently.
          console.log(`[VOManager] No in/out points returned from transcription: ${result.transcriptionError || 'unknown reason'}`);
          enqueueSnackbar(
            `Line ${key} audio uploaded, but its timing could not be detected${result.transcriptionError ? ` (${result.transcriptionError})` : ''}. Open the waveform editor to set the in/out manually, or try re-uploading.`,
            { variant: 'error', autoHideDuration: 15000 }
          );
        }

        // Persist the VO field writes.
        if (writes.length > 0) {
          try {
            await Promise.all(writes);
            console.log(`[VOManager] Successfully saved ${writes.length} VO fields`);
            if (hasTimestamps) {
              enqueueSnackbar(`Line ${key} uploaded and transcribed successfully`, { variant: 'success' });
            }
          } catch (saveError) {
            console.error(`[VOManager] Failed to save VO fields:`, saveError);
            enqueueSnackbar('Failed to save VO data', { variant: 'warning' });
          }
        }
      }
    } catch (err) {
      console.error(`Error uploading file for line ${key}:`, err);
      setError(`Error uploading file for line ${key}: ${err instanceof Error ? err.message : String(err)}`);
      enqueueSnackbar(`Error uploading line ${key}`, { variant: 'error' });
    } finally {
      setIsUploadingLine(null);
    }
  };

  // Add useEffect to load line audio files from localStorage
  useEffect(() => {
    if (activeSpreadsheetId) {
      try {
        const lineAudioStorage = localStorage.getItem(`lineAudioFiles_${activeSpreadsheetId}`);
        if (lineAudioStorage) {
          const lineAudios = JSON.parse(lineAudioStorage);
          setLineAudioFiles(lineAudios);
          console.log(`[VOManager] Loaded line audio files from localStorage for ${activeSpreadsheetId}:`, lineAudios);
        } else {
          console.log(`[VOManager] No line audio files found in localStorage for ${activeSpreadsheetId}`);
        }
      } catch (error) {
        console.error(`[VOManager] Error loading line audio files from localStorage:`, error);
      }
    }
  }, [activeSpreadsheetId]);

  // Add useEffect to load transcription result from localStorage (for original timestamp restoration)
  useEffect(() => {
    if (activeSpreadsheetId) {
      try {
        const storedResult = localStorage.getItem(`transcriptionResult_${activeSpreadsheetId}`);
        if (storedResult) {
          const parsedResult = JSON.parse(storedResult);
          setTranscriptionResult(parsedResult);
          console.log(`[VOManager] Loaded transcription result from localStorage for ${activeSpreadsheetId}`);
        } else {
          console.log(`[VOManager] No transcription result found in localStorage for ${activeSpreadsheetId}`);
        }
      } catch (error) {
        console.error(`[VOManager] Error loading transcription result from localStorage:`, error);
      }
    }
  }, [activeSpreadsheetId]);

  if (!activeSpreadsheetId) {
    return (
       <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', mt: 4, p: 2 }}>
         <Typography variant="h6" sx={{ mb: 2 }}>No Chart Date Selected</Typography>
         <Typography variant="body1" sx={{ mb: 2 }}>Please select a date on the Charts page first.</Typography>
         <Button 
           onClick={() => navigate('/')} 
           variant="contained"
           sx={{ 
             textTransform: 'none',
             fontWeight: 600
           }}
         >
           Go to Charts
         </Button>
       </Box>
    );
  }

  if (loading) {
     return (
       <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
         <CircularProgress />
       </Box>
     );
   }

  return (
    <Box sx={{ maxWidth: 800, margin: 'auto', mt: 4, p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body1" sx={{ fontWeight: 400 }}>Upload VO</Typography>
          <FormControlLabel
            control={
              <Switch
                checked={isGenerateMode}
                onChange={(e) => handleModeToggle(e.target.checked)}
                color="primary"
              />
            }
            label=""
          />
          <Typography variant="body1" sx={{ fontWeight: 400 }}>Generate VO</Typography>
        </Stack>
        
        {/* Rest of the top buttons */}
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {/* Show loading indicator when caching audio */}
          {!isCachingComplete && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 2 }}>
              <CircularProgress size={20} />
              <Typography variant="body2" color="text.secondary">
                Loading audio from cloud...
              </Typography>
            </Box>
          )}
          {isGenerateMode && (
            <>
              <Tooltip title="Generate Audio for All Entries Missing It">
                <span>
                  <IconButton
                    onClick={handleGenerateAll}
                    disabled={isGeneratingAll || loading}
                    color="primary"
                  >
                    {isGeneratingAll ? <CircularProgress size={24} /> : <AutoFixHighIcon />}
                  </IconButton>
                </span>
              </Tooltip>

              <Tooltip title="Voice Settings">
                <IconButton
                  onClick={() => setIsSettingsOpen(true)}
                  disabled={isGeneratingAll}
                  sx={{ color: '#000000' }}
                >
                  <SettingsIcon />
                </IconButton>
              </Tooltip>
            </>
          )}

          {/* Bilingual: VO language toggle. Swaps only the VO fields (script,
              audio, timing, gain); shared graphics/positions/settings are untouched.
              Shown only for charts flagged bilingual in the Chart Config. */}
          {isBilingual && (
            <Tooltip title="Voice-over language (shared graphics & positions stay the same)">
              <ToggleButtonGroup
                value={activeLanguage}
                exclusive
                size="small"
                onChange={(_e, val) => { if (val) handleSetLanguage(val as VoLanguage); }}
                disabled={isGeneratingAll}
                sx={{ mr: 1 }}
              >
                <ToggleButton value="en" sx={{ px: 1.5, py: 0.5 }}>EN</ToggleButton>
                <ToggleButton value="es" sx={{ px: 1.5, py: 0.5 }}>ES</ToggleButton>
              </ToggleButtonGroup>
            </Tooltip>
          )}

          <Tooltip title="Global Gain Settings">
            <IconButton
              onClick={() => setShowLimiterDialog(true)}
              disabled={isGeneratingAll}
              sx={{ color: '#000000' }}
            >
              <VolumeUpIcon />
            </IconButton>
          </Tooltip>

          <Button
            variant="outlined"
            onClick={() => setScriptDialogOpen(true)}
            disabled={isGeneratingAll}
            sx={{
              textTransform: 'uppercase',
              borderColor: '#000000',
              color: '#000000',
              '&:hover': {
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Upload Script
          </Button>

          <Button
            variant="outlined"
            onClick={() => navigate('/')}
            disabled={isGeneratingAll}
            sx={{
              textTransform: 'uppercase',
              borderColor: '#000000',
              color: '#000000',
              '&:hover': {
                borderColor: '#000000',
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              },
            }}
          >
            Charts
          </Button>
          
          {/* Add Export Button */}
          {/* <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button
              variant="outlined" // Changed from contained
              onClick={() => setScriptDialogOpen(true)} // Use the export all handler
              disabled={isGeneratingAll || (!isGenerateMode && !processedAudioUrl)}
              sx={{
                textTransform: 'none',
              }}
            >
              'Export All Clips'
            </Button>
          </Box> */}
        </Box>
      </Box>

      {/* Add back the error message alert */}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Display the main VU meter permanently instead of conditionally */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="caption" sx={{ display: 'block', mb: 0.5 }}>
          Audio Level {playingKey ? `- VO ${playingKey}` : ''}
        </Typography>
        <VUMeter 
          analyser={activeAnalyser} 
          isPlaying={!!playingKey} 
          width={300}
          height={24}
        />
      </Box>

      {/* Upload Audio File and Clear VO buttons */}
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        {!isGenerateMode && (
          <Button
            component="label"
            variant="contained"
            startIcon={<CloudUploadIcon />}
            disabled={isProcessing}
            sx={{
              backgroundColor: themeColor,
              color: '#000000',
              '&:hover': {
                backgroundColor: themeHoverColor,
              },
            }}
          >
            {isProcessing ? 'Processing...' : 'Upload Audio File'}
            <VisuallyHiddenInput
              type="file"
              accept="audio/*"
              onChange={handleFileUpload}
            />
          </Button>
        )}

        <Button
          variant="outlined"
          startIcon={<ClearIcon />}
          onClick={handleClearVO}
          disabled={isProcessing}
          sx={{
            borderColor: '#000000',
            color: '#000000',
            '&:hover': {
              borderColor: '#000000',
              backgroundColor: 'rgba(0, 0, 0, 0.04)',
            },
          }}
        >
          Clear VO
        </Button>
      </Box>

      <Box>
      <TableContainer
        component={Paper}
        sx={{
          border: '1px solid rgba(224, 224, 224, 1)',
          borderRadius: '4px',
        }}
      >
          <Table stickyHeader aria-label="vo manager table" size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '100px', textAlign: 'center', backgroundColor: 'black', color: 'white' }}>Controls</TableCell>
                <TableCell sx={{ width: '40px', backgroundColor: 'black', color: 'white' }}>Position</TableCell>
                <TableCell sx={{ width: '70px', textAlign: 'center', backgroundColor: 'black', color: 'white' }}>Gain</TableCell>
                <TableCell sx={{ width: '80px', textAlign: 'center', backgroundColor: 'black', color: 'white' }}>VO In</TableCell>
                <TableCell sx={{ width: '80px', textAlign: 'center', backgroundColor: 'black', color: 'white' }}>VO Out</TableCell>
                <TableCell sx={{ backgroundColor: 'black', color: 'white' }}>Script</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DISPLAY_ORDER.map((key) => (
                <TableRow
                  key={key}
                  hover
                  sx={{
                    '& > td, & > th': { // Target both td and th
                      height: '60px', // Apply fixed height
                      padding: '8px 12px',
                      // verticalAlign: 'top', // Ensure this line is removed or commented out if present
                    }
                  }}
                >
                  <TableCell sx={{ width: '100px', textAlign: 'center', borderRight: '1px solid rgba(224, 224, 224, 1)' }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        {/* Upload/Generate Button - shows generate icon in generate mode, upload icon in upload mode */}
                        {isGenerateMode ? (
                          // Generate mode: show generate button
                          <Tooltip title={generatedAudio[key] ? `Regenerate audio for ${['Tease', 'Intro', 'Outro'].includes(key) ? key : `Song ${key}`}` : `Generate audio for ${['Tease', 'Intro', 'Outro'].includes(key) ? key : `Song ${key}`}`}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                sx={{ padding: '4px' }}
                                onClick={() => handleGenerateLine(key)}
                                disabled={isGeneratingAll || isProcessing || isGeneratingLine === key || !voData[key]?.trim()}
                              >
                                {isGeneratingLine === key ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <AutoFixHighIcon fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : lineAudioFiles[key] ? (
                          // Upload mode with existing file: show delete button
                          <Tooltip title={`Delete override audio for ${['Tease', 'Intro', 'Outro'].includes(key) ? key : `Song ${key}`}`}>
                            <span>
                              <IconButton
                                size="small"
                                color="error"
                                sx={{ padding: '4px' }}
                                onClick={() => handleLineFileDelete(key)}
                                disabled={isGeneratingAll || isProcessing}
                              >
                                <ClearIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : (
                          // Upload mode without file: show upload button.
                          // Disabled until the line has real script text — the
                          // per-line upload endpoint requires lineText for STT,
                          // and an empty/default (placeholder) box would 400.
                          (() => {
                            const hasLineText = !!(voData[key] && voData[key].trim() !== '');
                            const label = ['Tease', 'Intro', 'Outro'].includes(key) ? key : `Song ${key}`;
                            return (
                              <Tooltip title={hasLineText ? `Upload override audio for ${label}` : `Add VO script text for ${label} before uploading audio`}>
                                <span>
                                  <IconButton
                                    component="label"
                                    size="small"
                                    color="primary"
                                    sx={{ padding: '4px' }}
                                    disabled={isGeneratingAll || isProcessing || isUploadingLine === key || !hasLineText}
                                  >
                                    {isUploadingLine === key ? (
                                      <CircularProgress size={16} />
                                    ) : (
                                      <UploadIcon />
                                    )}
                                    <VisuallyHiddenInput
                                      type="file"
                                      accept="audio/*"
                                      onChange={(e) => handleLineFileUpload(key, e)}
                                    />
                                  </IconButton>
                                </span>
                              </Tooltip>
                            );
                          })()
                        )}

                        <Tooltip title="Edit in Waveform Editor">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              sx={{ padding: '4px' }}
                              onClick={() => handleOpenWaveformEditor(key)}
                              disabled={ // Update this disabled logic
                                !( // Enable if EITHER generated audio exists OR default outro OR standard case with data
                                  (isGenerateMode && generatedAudio[key]) || // Case 0: Generate mode with generated audio
                                  (key === 'Outro' && (!voData[key] || voData[key].trim() === '')) || // Case 1: Is default Outro
                                  ( // Case 2: Standard case (upload mode)
                                    (lineAudioFiles[key] || processedAudioUrl) && // Needs an audio URL (line or main)
                                    voInPoints[key] && voOutPoints[key] && // Needs points
                                    !isNaN(parseFloat(voInPoints[key])) && !isNaN(parseFloat(voOutPoints[key])) // Points must be valid numbers
                                  )
                                )
                              }
                            >
                              <GraphicEqIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        
                        <Tooltip
                          title={
                            playingKey === key
                              ? "Stop Audio"
                              : isGenerateMode
                                ? (generatedAudio[key] ? "Play Audio" : "No generated audio")
                                : key === 'Outro' && (
                                    (!voData[key] || voData[key].trim() === '') ||
                                    (voInPoints[key] === '0' && voOutPoints[key] === '0')
                                  )
                                  ? "Play Default Outro (used when script is empty or in/out points are both 0)"
                                  : (!voInPoints[key] || !voOutPoints[key])
                                    ? "Missing in/out points"
                                    : "Play Audio"
                          }
                        >
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => handleVOButtonClick(key)}
                              disabled={isGeneratingAll || (
                                isGenerateMode
                                  ? !generatedAudio[key] // In generate mode: disabled if no generated audio
                                  : ((!voInPoints[key] || !voOutPoints[key]) && key !== 'Outro') // In upload mode: needs in/out points (except Outro)
                              )}
                              color={playingKey === key ? 'secondary' : 'primary'}
                              sx={{ padding: '4px' }}
                            >
                              {playingKey === key ? <StopIcon /> : <PlayArrowIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                      
                      {/* Small VU meter that appears in each row */}
                      <VUMeter 
                        analyser={playingKey === key ? activeAnalyser : null}
                        isPlaying={playingKey === key}
                        width={80}
                        height={16}
                      />
                    </Box>
                  </TableCell>

                  {/* Position Cell - moved here */}
                  <TableCell
                    component="th"
                    scope="row"
                    sx={{ 
                      width: '40px', 
                      fontWeight: 'bold', 
                      borderRight: '1px solid rgba(224, 224, 224, 1)', 
                      textAlign: 'center',
                      fontSize: '1.25rem', // Increase font size by 25%
                      verticalAlign: 'middle', // Add vertical alignment
                      backgroundColor: 'black',
                      color: 'white'
                    }}
                  >
                    {key}
                  </TableCell>

                  {/* Gain Cell */}
                  <TableCell
                        onClick={() => handleGainClick(key)}
                        sx={{
                          width: '70px',
                          borderRight: '1px solid rgba(224, 224, 224, 1)',
                          textAlign: 'center',
                          verticalAlign: 'middle',
                        }}
                      >
                        {editingCell?.key === `gain_${key}` ? (
                          <TextField
                            value={editingCell.value}
                            onChange={(e) => handleCellEditChange(`gain_${key}`, e.target.value)}
                            onBlur={(e) => handleGainSave(key, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleGainSave(key, (e.target as HTMLInputElement).value);
                              } else if (e.key === 'Escape') {
                                setEditingCell(null);
                              }
                            }}
                            placeholder="0dB"
                            size="small"
                            variant="outlined"
                            autoFocus
                            type="number"
                            inputProps={{
                              min: -24,
                              max: 24,
                              step: 0.5,
                              style: { textAlign: 'center' }
                            }}
                            sx={{
                              width: '60px',
                              '& .MuiInputBase-root': {
                                padding: '2px 4px',
                              },
                              '& input[type=number]': {
                                MozAppearance: 'textfield'
                              },
                              '& input[type=number]::-webkit-outer-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0
                              },
                              '& input[type=number]::-webkit-inner-spin-button': {
                                WebkitAppearance: 'none',
                                margin: 0
                              }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: lineGainSettings[key] !== undefined ? 'inherit' : 'text.disabled', cursor: 'pointer' }}>
                            {(() => {
                              // Special case for Outro: default to 0dB if using default VO (no VO text) and P13 is not set
                              if (key === 'Outro' && (!voData[key] || voData[key].trim() === '') && lineGainSettings[key] === undefined) {
                                return '0.0dB';
                              }
                              // Normal case: show line-specific gain if set, otherwise show global gain
                              return lineGainSettings[key] !== undefined
                                ? `${lineGainSettings[key] > 0 ? '+' : ''}${lineGainSettings[key].toFixed(1)}dB`
                                : `${limiterSettings.gain > 0 ? '+' : ''}${limiterSettings.gain.toFixed(1)}dB`;
                            })()}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell
                        onClick={() => handleInPointClick(key)}
                        sx={{
                          width: '80px',
                          borderRight: '1px solid rgba(224, 224, 224, 1)',
                          textAlign: 'center',
                          verticalAlign: 'middle', // Add vertical alignment
                        }}
                      >
                        {editingCell?.key === `in_${key}` ? (
                          <TextField
                            value={editingCell.value}
                            onChange={(e) => handleCellEditChange(`in_${key}`, e.target.value)}
                            onBlur={(e) => handleInPointSave(key, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleInPointSave(key, (e.target as HTMLInputElement).value);
                              } else if (e.key === 'Escape') {
                                setEditingCell(null);
                              }
                            }}
                            placeholder="IN"
                            size="small"
                            variant="outlined"
                            autoFocus
                            sx={{
                              width: '70px',
                              '& .MuiInputBase-root': {
                                padding: '2px 4px',
                              }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: (key === 'Outro' && (!voData[key] || voData[key].trim() === '')) ? 'text.disabled' : voInPoints[key] ? 'inherit' : 'text.disabled', cursor: 'pointer' }}>
                            {/* Display default 0.00 for empty Outro, otherwise state value */}
                            {(key === 'Outro' && (!voData[key] || voData[key].trim() === ''))
                              ? '0.00'
                              : (typeof voInPoints[key] === 'number' || (typeof voInPoints[key] === 'string' && !isNaN(parseFloat(voInPoints[key]))))
                              ? parseFloat(voInPoints[key].toString()).toFixed(2)
                                : (voInPoints[key] || 'IN')}
                        </Typography>
                        )}
                      </TableCell>

                      <TableCell
                        onClick={() => handleOutPointClick(key)}
                        sx={{
                          width: '80px',
                          borderRight: '1px solid rgba(224, 224, 224, 1)',
                          textAlign: 'center',
                          verticalAlign: 'middle', // Add vertical alignment
                        }}
                      >
                        {editingCell?.key === `out_${key}` ? (
                          <TextField
                            value={editingCell.value}
                            onChange={(e) => handleCellEditChange(`out_${key}`, e.target.value)}
                            onBlur={(e) => handleOutPointSave(key, e.target.value)}
                            onKeyPress={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleOutPointSave(key, (e.target as HTMLInputElement).value);
                              } else if (e.key === 'Escape') {
                                setEditingCell(null);
                              }
                            }}
                            placeholder="OUT"
                            size="small"
                            variant="outlined"
                            autoFocus
                            sx={{
                              width: '70px',
                              '& .MuiInputBase-root': {
                                padding: '2px 4px',
                              }
                            }}
                          />
                        ) : (
                          <Typography variant="body2" sx={{ color: (key === 'Outro' && (!voData[key] || voData[key].trim() === '')) ? 'text.disabled' : voOutPoints[key] ? 'inherit' : 'text.disabled', cursor: 'pointer' }}>
                            {/* Display the per-chart default outro duration for empty Outro, otherwise state value */}
                            {(key === 'Outro' && (!voData[key] || voData[key].trim() === ''))
                             ? getDefaultOutroDuration(currentChartConfig?.voSubfolder).toFixed(2)
                             : (typeof voOutPoints[key] === 'number' || (typeof voOutPoints[key] === 'string' && !isNaN(parseFloat(voOutPoints[key]))))
                              ? parseFloat(voOutPoints[key].toString()).toFixed(2)
                               : (voOutPoints[key] || 'OUT')}
                        </Typography>
                        )}
                      </TableCell>

                  <TableCell
                    onClick={() => !editingCell && handleCellClick(key)}
                    sx={{
                      cursor: isGeneratingAll ? 'default' : 'pointer',
                      minWidth: '300px',
                      whiteSpace: 'pre-wrap',
                      verticalAlign: 'middle' // Add vertical alignment
                    }}
                  >
                    {editingCell?.key === key ? (
                      <TextField
                        value={editingCell.value}
                        onChange={(e) => handleCellEditChange(key, e.target.value)}
                        onBlur={(e) => handleSave(key, e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            handleSave(key, (e.target as HTMLInputElement).value);
                          } else if (e.key === 'Escape') {
                            setEditingCell(null);
                          }
                        }}
                        multiline
                        fullWidth
                        size="small"
                        variant="outlined"
                        autoFocus
                        placeholder={key === 'Outro' ? getDefaultOutroText(currentChartConfig?.defaultOutroText || '') : undefined}
                        sx={{
                          padding: 0,
                          '& .MuiInputBase-root': {
                            padding: '6px 8px',
                          },
                          '& .MuiInputBase-input::placeholder': {
                            color: '#999',
                            fontStyle: 'italic'
                          }
                        }}
                      />
                    ) : (
                      <Typography variant="body2" sx={{ 
                        color: voData[key] ? 'inherit' : 'text.disabled', 
                        minHeight: '20px',
                        fontStyle: key === 'Outro' && !voData[key] ? 'italic' : 'normal'
                      }}>
                        {voData[key] || (key === 'Outro' ? getDefaultOutroText(currentChartConfig?.defaultOutroText || '') : '-')}
                      </Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

      <SettingsDialog
        open={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
        currentSettings={voiceSettings}
        onSave={handleSaveSettings}
      />

      <ScriptManager
        open={scriptDialogOpen}
        onClose={() => setScriptDialogOpen(false)}
        onSave={handleScriptSave}
      />

      <LimiterSettingsDialog
        open={showLimiterDialog}
        onClose={() => setShowLimiterDialog(false)}
        onSave={handleLimiterChange}
        initialSettings={limiterSettings}
      />

      {/* Progress indicator */}
      {isProcessing && (
        <Box sx={{ width: '100%', mt: 2 }}>
          <LinearProgress variant="determinate" value={uploadProgress} />
          <Typography variant="body2" color="text.secondary" align="center">
            {uploadProgress}%
          </Typography>
        </Box>
      )}

      {/* Transcription result */}
      {isUploadSuccess && transcriptionResult && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Transcription Result
          </Typography>
          <Typography variant="body1">
            {transcriptionResult.text}
          </Typography>
        </Box>
      )}

      {/* STT Word-level timestamps */}
      {transcriptionResult && transcriptionResult.words && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            STT Word-level timestamps {transcriptionResult.words.length} words
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Word</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>Start</th>
                  <th style={{ border: '1px solid #ddd', padding: '8px', textAlign: 'left' }}>End</th>
                </tr>
              </thead>
              <tbody>
                {transcriptionResult.words.map((word, index) => {
                  // Convert to numbers and ensure they are valid
                  const startTime = typeof word.startTime === 'string' ? parseFloat(word.startTime) : 
                    typeof word.startTime === 'number' ? word.startTime : 
                    ((word.startTime as any)?.seconds || 0) + ((word.startTime as any)?.nanos || 0) / 1e9;
                  
                  const endTime = typeof word.endTime === 'string' ? parseFloat(word.endTime) : 
                    typeof word.endTime === 'number' ? word.endTime : 
                    ((word.endTime as any)?.seconds || 0) + ((word.endTime as any)?.nanos || 0) / 1e9;
                  
                  return (
                    <tr key={index}>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{word.word}</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{!isNaN(startTime) ? startTime.toFixed(2) : 'N/A'}s</td>
                      <td style={{ border: '1px solid #ddd', padding: '8px' }}>{!isNaN(endTime) ? endTime.toFixed(2) : 'N/A'}s</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
          <Button 
            onClick={() => console.log('TranscriptionResult:', transcriptionResult)} 
            variant="outlined" 
            size="small" 
            sx={{ mt: 1 }}
          >
            Debug
          </Button>
        </Box>
      )}

      {/* Final timestamps */}
      {transcriptionResult && transcriptionResult.timestamps && Object.keys(transcriptionResult.timestamps).length > 0 && (
        <Box sx={{ mt: 3, p: 2, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Gemini-processed Timestamps
          </Typography>
          <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Line</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>In Point</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Out Point</th>
                  <th style={{ textAlign: 'left', padding: '8px' }}>Duration</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(transcriptionResult.timestamps).map(([key, value]) => {
                  // Convert to numbers and ensure they are valid
                  const inPoint = typeof value.inPoint === 'string' ? parseFloat(value.inPoint) : value.inPoint;
                  const outPoint = typeof value.outPoint === 'string' ? parseFloat(value.outPoint) : value.outPoint;
                  
                  if (isNaN(inPoint) || isNaN(outPoint)) return null;
                  
                  return (
                    <tr key={key} style={{ borderBottom: '1px solid #ddd' }}>
                      <td style={{ padding: '8px' }}>{key}</td>
                      <td style={{ padding: '8px' }}>{inPoint.toFixed(2)}s</td>
                      <td style={{ padding: '8px' }}>{outPoint.toFixed(2)}s</td>
                      <td style={{ padding: '8px' }}>{(outPoint - inPoint).toFixed(2)}s</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Box>
        </Box>
      )}

      {/* Waveform Editor Dialog */}
      {isEditorOpen && editorData && (
        <WaveformEditorDialog
          open={isEditorOpen}
          onClose={handleCloseEditor} // Use the new handler
          onSave={handleSaveEditedTimestamps}
          audioUrl={editorData.audioUrl ?? ''} // Provide fallback
          initialInPoint={editorData.initialInPoint ?? 0} // Provide fallback
          initialOutPoint={editorData.initialOutPoint ?? 0} // Provide fallback
          initialGain={editorData.initialGain ?? 0} // Read from editorData, provide fallback
          lineKey={editorData.lineKey} // Use lineKey (already correct here)
          songTitle={editorData.songTitle} // Keep as is (optional in props)
          songArtist={editorData.songArtist} // Keep as is (optional in props)
          peaks={processedAudioPeaks ?? undefined} // Convert null to undefined
          onPeaksGenerated={handlePeaksGenerated} // Pass down the handler
        />
      )}

    </Box>
    </Box>
  );
};

export default VOManager; 